/**
 * Credit Cost Utilities
 *
 * Pricing is loaded from the database (model_registry table) at startup via
 * `initializePricing()`. The hardcoded FALLBACK_COSTS below are only used
 * until the API response arrives (typically <1 second after login).
 *
 * To add a new model's pricing, insert a row into `model_registry` via a
 * Supabase migration — no frontend code changes needed.
 *
 * Credit costs vary by resolution tier:
 * - 480p (0.75x - used by Wan Animate)
 * - 580p (base for Wan Animate, treated as 720p equivalent)
 * - 720p / 512px (base)
 * - 1080p / 1024px (1.5x)
 * - 1440p / 2048px (2x)
 * - 4K / 4096px (3x)
 */

// Resolution tiers for credit calculation
export type ResolutionTier = '480p' | '580p' | '720p' | '1080p' | '1440p' | '4k';

// Resolution tier multipliers
const RESOLUTION_MULTIPLIERS: Record<ResolutionTier, number> = {
  '480p': 0.75,  // Lower than base for Wan Animate
  '580p': 1,     // Wan Animate base resolution
  '720p': 1,
  '1080p': 1.5,
  '1440p': 2,
  '4k': 3,
};

// Base costs per model at 720p resolution (per second for video, per generation for images)
export interface ModelBaseCost {
  base720p: number;
  unit: 'generation' | 'second';
  tier: 'fast' | 'pro' | 'base';
  // Some providers (e.g. fal.ai Wan 2.7) charge a flat per-second rate regardless
  // of resolution. For these models, skip the RESOLUTION_MULTIPLIERS lookup so
  // we don't overcharge users at 1080p+.
  flatRate?: boolean;
}

/**
 * Hunyuan Video 1.5 always renders 121 frames (~5 seconds) — `num_frames`
 * defaults to and maxes out at 121 and there is no `duration` field in either
 * of its fal schemas. The quote multiplies this, not the node's setting.
 * Mirror of the constant in server/services/CreditService.ts.
 */
export const HUNYUAN_15_FIXED_SECONDS = 5;

// ---------------------------------------------------------------------------
// Dynamic pricing store — populated from the database via initializePricing()
// ---------------------------------------------------------------------------
const MODEL_BASE_COSTS: Record<string, ModelBaseCost> = {};
let _pricingInitialized = false;

/**
 * Populate MODEL_BASE_COSTS from model_registry rows returned by the server.
 * Called once from CreditsContext after fetching /api/credits/pricing.
 *
 * ## Why this is not a plain loop any more
 *
 * `model_registry`'s primary key is (provider, model_id), so the SAME model id
 * can exist under two providers — and six of them did, at divergent prices:
 *
 *     gemini-3.1-flash-image   falai 37   google 20
 *     gemini-nano-banana       falai 69   google 12
 *     gemini-3-pro-image-preview falai 69 google 40
 *     grok-image               falai 32   xai    9
 *     grok-video               falai 33   xai   21
 *     veo-3                    falai 160  google 120
 *
 * `MODEL_BASE_COSTS` is keyed by the BARE model id, and this function used to
 * write `MODEL_BASE_COSTS[m.modelId] = ...` in payload order — so whichever
 * copy the server happened to list LAST won. The server's own
 * `refreshModelCache()` selects with no ORDER BY, which means the winner was
 * decided by Postgres's physical row order: for `gemini-3.1-flash-image` the
 * dead falai row (37) landed last and overwrote google's 20, an 85% inflated
 * quote on the DEFAULT Gemini model, held back by nothing but row order.
 *
 * ## The rule
 *
 * A model id that arrives under MORE THAN ONE provider is ambiguous, and this
 * function has no way to adjudicate it — the frontend node registry carries no
 * provider field, so there is nothing here that knows which copy is live.
 * Rather than guess (any guess is decided by ordering, which is the bug), the
 * ambiguous id is SKIPPED and the curated `FALLBACK_COSTS` entry below is left
 * standing. Those hardcoded values are hand-written against the live provider,
 * so skipping lands on the correct price for every one of the six pairs above
 * — and, crucially, the outcome no longer depends on row order at all.
 *
 * A skipped id is logged as an error: it means model_registry is publishing a
 * duplicate, which is a data defect to fix with a migration (130 deactivated
 * these six), not something to paper over here.
 *
 * ## Why not key MODEL_BASE_COSTS by `provider:modelId`
 *
 * That is the more correct shape and it is deliberately NOT what this does.
 * Every lookup — `calculateCreditCostWithResolution`, `selectionCostUtils`, the
 * node badges — receives a bare model id and nothing else; `src/config/
 * nodeRegistry.ts` has no `provider` field at all, so a provider-keyed store
 * would mean threading a new provider argument through every quoting path,
 * sourced from a frontend copy of the SERVER node registry. The failure mode of
 * that diff is a call site that forgets the argument, which returns `undefined`
 * and quotes 0 credits — silently worse than the bug being fixed. The keying is
 * therefore left alone and the WRITER is made order-independent instead.
 */
export function initializePricing(
  models: Array<{ provider: string; modelId: string; creditsPerUnit: number; unit: string; operationType: string }>
): void {
  // modelId -> provider -> row. Two rows sharing a provider are the same
  // (provider, model_id) row and collapse; only DISTINCT providers are a
  // genuine collision.
  const byModelId = new Map<string, Map<string, (typeof models)[number]>>();
  for (const m of models) {
    let byProvider = byModelId.get(m.modelId);
    if (!byProvider) {
      byProvider = new Map();
      byModelId.set(m.modelId, byProvider);
    }
    byProvider.set(m.provider, m);
  }

  const shadowed: string[] = [];
  let loaded = 0;

  for (const [modelId, byProvider] of byModelId) {
    if (byProvider.size > 1) {
      shadowed.push(
        `${modelId} (${[...byProvider.values()]
          .map((r) => `${r.provider}=${r.creditsPerUnit}`)
          .join(', ')})`,
      );
      continue;
    }
    const m = [...byProvider.values()][0];
    // Infer tier from operation type heuristic (not critical — only used for display)
    const tier: ModelBaseCost['tier'] =
      modelId.includes('fast') || modelId.includes('turbo') || modelId.includes('flash')
        ? 'fast'
        : modelId.includes('pro') || modelId.includes('max')
          ? 'pro'
          : 'base';
    MODEL_BASE_COSTS[modelId] = {
      base720p: m.creditsPerUnit,
      unit: m.unit as 'generation' | 'second',
      tier,
    };
    loaded++;
  }

  _pricingInitialized = true;
  if (shadowed.length > 0) {
    console.error(
      `[CreditCostUtils] model_registry publishes ${shadowed.length} model id(s) under more than one provider; ` +
        `keeping the hardcoded fallback price for each rather than letting row order decide: ${shadowed.join('; ')}`,
    );
  }
  console.log(
    `[CreditCostUtils] Pricing initialized with ${loaded} models from DB` +
      (shadowed.length > 0 ? ` (${shadowed.length} ambiguous id(s) skipped)` : ''),
  );
}

/** Whether pricing has been loaded from the server */
export function isPricingInitialized(): boolean {
  return _pricingInitialized;
}

// ---------------------------------------------------------------------------
// Fallback costs — used before the API response arrives.
// These will be overwritten by initializePricing() once the data loads.
// Keep this list as a safety net so the UI never shows 0 credits.
// ---------------------------------------------------------------------------
const FALLBACK_COSTS: Record<string, ModelBaseCost> = {
  // VEO Video Models (per second at 720p)
  // Vertex AI VEO models (google provider)
  // Google pricing (ai.google.dev/gemini-api/docs/pricing, verified 2026-09-01):
  //   Veo 3.1 Standard: $0.40/s at 720p AND 1080p, $0.60/s at 4K
  //   Veo 3.1 Fast:     $0.10/s @720p, $0.12/s @1080p, $0.30/s @4K
  //   Veo 3.1 Lite:     $0.05/s @720p, $0.08/s @1080p, no 4K
  // Premium-video basis for Google rows is a 50% margin: ceil(usd * 2 / 0.00333).
  //
  // The deferral that used to sit here — "Google has since raised Veo 3.1
  // Standard to $0.40/sec ... revisit only via explicit pricing review" — was
  // ACTED ON in migration 120. Standard had been left at 120 cr/s, i.e. $0.3996
  // of revenue against $0.40 of cost: a zero-to-negative margin on the most
  // expensive video node in the product. It is now 241 cr/s.
  //
  // Standard CANNOT use the generic ladder: Google charges the SAME $0.40 at
  // 720p and 1080p, and 4K is only 1.5x that — where the ladder would apply
  // 1.5x and 3x. Using it would have turned an undercharge into a 50% (1080p)
  // and 100% (4K) OVERcharge. See calculateVeo31StandardCreditCost() below.
  'veo-3.1-fast-generate-preview': { base720p: 60, unit: 'second', tier: 'fast' },
  'veo-3.1-generate-preview': { base720p: 241, unit: 'second', tier: 'pro' },
  // Gemini Omni (Google direct) — 720p, $0.10/sec, priced identically to
  // veo-3.1-fast-generate-preview (60 cr/sec @ 720p, 50% margin). 720p multiplier = 1×.
  //
  // Google's pricing page (fetched 2026-09-01) gives the rate as $17.50 per 1M
  // video output tokens "calculated at a rate of 5,792 tokens per second of 720p
  // video" → 5792 × 17.50 / 1e6 = $0.10136/sec, which the page itself rounds to
  // "approximately $0.10 per second". BOTH ids below carry that same rate, so
  // 1.1 inherits the preview's 60 unchanged.
  //
  // Note the basis formula ceil(usd × 2 / 0.00333) yields 61 at $0.10, not 60.
  // 60 is the established house value for every $0.10/sec Google row
  // (veo-3.1-fast, veo-3-fast, the omni preview) and is kept for consistency —
  // 60 cr = $0.1998 revenue on $0.10 cost, i.e. 49.95% vs the nominal 50%. A
  // 1-credit correction across that whole family is a pricing review of its own,
  // NOT something to introduce here on one new id (which would leave two models
  // at an identical cost priced differently).
  'gemini-omni-1.1-flash': { base720p: 60, unit: 'second', tier: 'fast' },
  // Shut down by Google 2026-09-30 and dropped from the node dropdown on
  // 2026-09-01. This row MUST STAY: saved flows that pin the id still quote
  // through it, and removing it would make them price as an unknown model.
  'gemini-omni-flash-preview': { base720p: 60, unit: 'second', tier: 'fast' },
  // VEO 3.1 Lite — Google's cheapest tier ($0.05/sec @720p, $0.08/sec @1080p, no 4K).
  // ~35% margin: ceil($0.05 × 300/0.65) = 24 cr/s; 1080p applies the standard 1.5× → 36 cr/s.
  // Migration 124 aligned Lite to the 50% basis every other Google row uses.
  // $0.05/s @720p -> 31, $0.08/s @1080p -> 49 (an explicit table, because the
  // 1.5x ladder off a 31 base gives 47, not 49). Lite has no 4K tier.
  'veo-3.1-lite-generate-preview': { base720p: 31, unit: 'second', tier: 'fast' },
  'veo-3-fast': { base720p: 60, unit: 'second', tier: 'fast' },
  // 'veo-3-pro' is NOT a separate product: server/routes/veo.ts maps the UI's
  // 'veo-3.1-generate-preview' onto it for the credit reservation (see migration
  // 019, "Used in veo.ts modelMapping but missing from registry"). It therefore
  // carries VEO 3.1 Standard traffic and must hold the same 241 — the client
  // quotes under the UI id, the server bills under this one.
  'veo-3-pro': { base720p: 241, unit: 'second', tier: 'pro' },
  // 'veo-3' is legacy and unreachable from the UI (the Veo node offers only the
  // three 3.1 tiers). Left at the old Veo 3.0 basis deliberately.
  'veo-3': { base720p: 120, unit: 'second', tier: 'pro' },
  // fal.ai VEO models (falai provider) - must match DB model_registry IDs
  // fal.ai VEO 3.1 Fast is priced by AUDIO STATE too, migration 131. fal charges
  // $0.10/s (720p/1080p) and $0.30/s (4K) without audio, $0.15 and $0.35 with.
  // Real rates come from calculateFalVeo31CreditCost(); these are the 720p
  // baselines. Was a single flat 80 that the generic ladder then scaled.
  'veo-3.1-fast': { base720p: 61, unit: 'second', tier: 'fast' },
  'veo-3.1-fast-audio': { base720p: 91, unit: 'second', tier: 'fast' },
  // fal.ai VEO 3.1 Standard is priced by AUDIO STATE, migration 124. fal charges
  // $0.20/s without audio and $0.40/s with it, so one number cannot be right for
  // both. 'veo-3.1' means audio OFF; 'veo-3.1-audio' means audio ON - the same
  // `-audio` SKU pattern kling-2.6-audio has used since migration 005.
  // Real rates come from calculateFalVeo31CreditCost(); these are baselines.
  'veo-3.1': { base720p: 120, unit: 'second', tier: 'pro' },
  'veo-3.1-audio': { base720p: 241, unit: 'second', tier: 'pro' },
  'veo-3-falai': { base720p: 160, unit: 'second', tier: 'pro' },   // Legacy fal.ai VEO 3
  
  // Image Models (per generation at 512px)
  // Updated Mar 2026: Gemini models now route through Google direct API (not fal.ai)
  'gemini-nano-banana': { base720p: 12, unit: 'generation', tier: 'base' },       // Banana Stable: Google $0.039/img → 12cr base, 18cr@1K (35% margin)
  'gemini-2.5-flash-image': { base720p: 12, unit: 'generation', tier: 'base' },  // API model ID for gemini-nano-banana
  'gemini-nano-banana-pro': { base720p: 40, unit: 'generation', tier: 'pro' },   // Banana Pro: Google $0.134/img → 40cr base, 60cr@1K (33% margin)
  'gemini-3-pro-image-preview': { base720p: 40, unit: 'generation', tier: 'pro' }, // Banana Pro: Google $0.134/img → 40cr base, 60cr@1K (33% margin)
  'gemini-3.1-flash-image': { base720p: 20, unit: 'generation', tier: 'base' },  // Banana 2 Fast: Google $0.045/img → 20cr (33% margin)
  'gemini-3.1-flash-lite-image': { base720p: 15, unit: 'generation', tier: 'fast' }, // Banana 2 Lite: Google $0.0336/img → 15cr (33% margin)
  'gpt-image-1': { base720p: 9, unit: 'generation', tier: 'base' },              // GPT Image 1: API $0.02 = 6cr cost → 9cr (33% margin)
  'gpt-image-1.5': { base720p: 18, unit: 'generation', tier: 'pro' },            // GPT Image 1.5: API $0.04 = 12cr cost → 18cr (33% margin)
  // gpt-image-2 base values used for display when quality is unknown. Real
  // pricing is per-(model × quality × resolution) via calculateGPTImageCreditCost().
  'gpt-image-2': { base720p: 24, unit: 'generation', tier: 'pro' },              // GPT Image 2: medium@1024 baseline. Use calculateGPTImageCreditCost for accurate cost.
  // GPT Image 1 Mini — cheapest OpenAI image model. low@1024 = $0.005, med = ~$0.02, high = $0.052.
  // Default tier (low) → ~3cr at 35% margin. See calculateGPTImageCreditCost for full table.
  'gpt-image-1-mini': { base720p: 3, unit: 'generation', tier: 'fast' },          // GPT Image 1 Mini (low): API $0.005 = 1.5cr cost → 3cr (50% margin)
  // DALL-E 2/3 removed from OpenAI API on 2026-05-12 — no longer routed.
  // Imagen 3 entries removed (no node uses them; Imagen 4 family is added separately).

  // Inpainting (per generation) - fixed cost
  'imagen-3-capability': { base720p: 3, unit: 'generation', tier: 'base' },
  
  // fal.ai FLUX Image Models (Black Forest Labs via fal.ai)
  // Pricing: https://fal.ai/pricing
  // Updated Feb 2026: Repriced for ~35% margin
  // NOTE: The knods id 'flux-2-max' was historically 'flux-2-pro' — renamed in Jun 2026
  // so we could add the real BFL FLUX.2 [pro] tier as a separate model.
  // Full API IDs
  'fal-ai/flux-2-max': { base720p: 32, unit: 'generation', tier: 'pro' },        // fal.ai $0.07/MP = 21cr cost → 32cr (34% margin)
  'fal-ai/flux-2-pro': { base720p: 14, unit: 'generation', tier: 'pro' },        // BFL FLUX.2 [pro]: $0.03 first MP → ceil($0.03×450) = 14cr base; 1080p $0.045 → 21cr via the 1.5× ladder (35.7% margin). Migration 119 — do NOT copy Max's 32 here.
  'fal-ai/flux-2/turbo': { base720p: 14, unit: 'generation', tier: 'base' },     // API $0.03 = 9cr cost → 14cr (36% margin)
  'fal-ai/flux-2/flash': { base720p: 5, unit: 'generation', tier: 'base' },      // API $0.01 = 3cr cost → 5cr (40% margin)
  // Short IDs (used in UI)
  'flux-2-max': { base720p: 32, unit: 'generation', tier: 'pro' },               // fal.ai $0.07/MP = 21cr cost → 32cr (34% margin)
  'flux-2-pro': { base720p: 14, unit: 'generation', tier: 'pro' },               // BFL FLUX.2 [pro]: $0.03 first MP ⇒ 14cr base, 21cr @ 1080p (35.7% margin). The DB row said 32 (Max's price) until migration 119 — see that header.
  'flux-2-turbo': { base720p: 14, unit: 'generation', tier: 'base' },            // API $0.03 = 9cr cost → 14cr (36% margin)
  'flux-2-flash': { base720p: 5, unit: 'generation', tier: 'base' },             // API $0.01 = 3cr cost → 5cr (40% margin)
  // FLUX Pro Fill (Inpainting): $0.02/megapixel
  'flux-pro-fill': { base720p: 10, unit: 'generation', tier: 'pro' },            // API $0.02 = 6cr cost → 10cr (40% margin)
  
  // fal.ai Image Models (per generation)
  // Pricing: https://fal.ai/pricing
  // Z-Image Turbo: ~$0.015/image, super fast
  'z-image-turbo': { base720p: 7, unit: 'generation', tier: 'base' },             // API $0.015 = 4.5cr cost → 7cr (36% margin)
  // Qwen Image: $0.02/megapixel
  'qwen-image': { base720p: 9, unit: 'generation', tier: 'base' },                // API $0.02 = 6cr cost → 9cr (33% margin)
  // Qwen Image Edit: $0.03/megapixel (image-to-image)
  'qwen-image-edit': { base720p: 14, unit: 'generation', tier: 'base' },          // fal.ai $0.03/MP = 9cr cost → 14cr (36% margin)
  // Seedream 4.5: $0.04/image
  'seedream-4.5': { base720p: 18, unit: 'generation', tier: 'base' },             // fal.ai $0.04/img = 12cr cost → 18cr (33% margin)
  // Recraft V3: $0.04/image ($0.08 for vector)
  'recraft-v3': { base720p: 18, unit: 'generation', tier: 'pro' },               // API $0.04 = 12cr cost → 18cr (33% margin)
  // Recraft V4 (Feb 2026): $0.04/image — 1MP fast tier
  'recraft-v4': { base720p: 19, unit: 'generation', tier: 'pro' },               // API $0.04 = 12cr cost → 19cr (37% margin)
  // Recraft V4 Pro (Feb 2026): $0.25/image — 4MP print-ready
  'recraft-v4-pro': { base720p: 116, unit: 'generation', tier: 'pro' },          // API $0.25 = 75cr cost → 116cr (35% margin)
  // Grok Imagine Image (xAI direct API): $0.02/image
  'grok-image': { base720p: 9, unit: 'generation', tier: 'base' },                // API $0.02 = 6cr cost → 9cr (33% margin)
  'grok-image-pro': { base720p: 22, unit: 'generation', tier: 'pro' },            // Migration 120: raised 18 → 22 to MATCH grok-imagine-image-quality, which ProviderService rewrites this alias to before the call. $0.05/img, same upstream request, same price.
  // Grok Imagine Image Quality — xAI's higher-quality image tier, $0.05/image.
  // Selectable in the Grok Image node as "Grok Image Quality". Internal route
  // already maps grok-image-pro → grok-imagine-image-quality, but we now expose
  // this ID directly so users can pick the explicit higher-tier model.
  'grok-imagine-image-quality': { base720p: 22, unit: 'generation', tier: 'pro' },// API $0.05 = 15cr cost → 22cr (35% margin)
  // Grok Imagine Image 2.0 — the low/1k floor ($0.04 → 18cr). The real price is
  // per (quality × resolution) + input images: calculateGrokImage2CreditCost().
  'grok-imagine-image-2.0': { base720p: 18, unit: 'generation', tier: 'pro' },
  // Qwen Image 2.0: $0.035/image
  'qwen-image-2': { base720p: 16, unit: 'generation', tier: 'base' },            // API $0.035 = 10.5cr cost → 16cr (34% margin)
  // Qwen Image 2.0 Pro: $0.075/image — highest-fidelity tier, native 2048×2048
  'qwen-image-2-pro': { base720p: 35, unit: 'generation', tier: 'pro' },         // API $0.075 = 22.5cr cost → 35cr (36% margin)
  // Seedream 5.0 Lite: ~$0.04/image (estimated)
  'seedream-5-lite': { base720p: 18, unit: 'generation', tier: 'base' },          // API $0.04 = 12cr cost → 18cr (33% margin)
  // GLM-Image: $0.05/megapixel (~$0.05 for 1024x1024)
  'glm-image': { base720p: 23, unit: 'generation', tier: 'base' },               // fal.ai $0.05/MP = 15cr cost → 23cr (35% margin)
  // Kling Image O3: $0.028/image at 1K AND 2K, double at 4K. base720p is the
  // 1K/2K baseline; the 4K tier is applied by calculateKlingImageCreditCost()
  // because fal's curve (2x at 4K) is not the generic multiplier curve (3x).
  'kling-image-o3': { base720p: 13, unit: 'generation', tier: 'base' },          // API $0.028 = 8.4cr cost → 13cr (35% margin)
  
  // fal.ai Video Models (per second at 720p)
  // Pricing: https://fal.ai/pricing
  // Kling 2.6 Pro: $0.07/sec (audio off), $0.14/sec (audio on)
  // The actual selectedModel id is 'kling-2.6'; falai.ts maps the audio variant
  // to 'kling-2.6-audio'. Old 'kling-2.6-pro[-audio]' keys kept as aliases for
  // any legacy callers (e.g. NodeAdapters credit-preview maps).
  // kling-2.6 / kling-2.6-audio mirror the model_registry rows the server bills
  // (migrations 004/005/033: 28 and 56 cr/s). Migration 054 repriced only the
  // legacy -pro ids, so a 32/65 fallback here over-quoted every Kling 2.6 run.
  'kling-2.6': { base720p: 28, unit: 'second', tier: 'pro' },                    // API $0.07 = 21cr cost → 28cr (server row)
  'kling-2.6-audio': { base720p: 56, unit: 'second', tier: 'pro' },              // API $0.14 = 42cr cost → 56cr (server row)
  'kling-2.6-pro': { base720p: 32, unit: 'second', tier: 'pro' },                // legacy id, own row (migration 054: 32)
  'kling-2.6-pro-audio': { base720p: 65, unit: 'second', tier: 'pro' },          // legacy id, own row (migration 054: 65)
  // Kling 2.5 Turbo: $0.07/sec
  'kling-2.5-turbo': { base720p: 32, unit: 'second', tier: 'fast' },              // API $0.07 = 21cr cost → 32cr (34% margin)
  // Kling 3.0 Standard: $0.084/sec (audio off), $0.112/sec (audio on)
  // Verified via WebFetch on fal.ai 2026-06-02; prior pricing assumed double
  // these rates ($0.168/$0.224) which produced a ~100% overcharge. Recalibrated
  // to 35% margin: ceil(price*300/0.65).
  'kling-3.0-standard': { base720p: 39, unit: 'second', tier: 'pro' },           // API $0.084 = 25.2cr → 39cr (35% margin)
  'kling-3.0-standard-audio': { base720p: 52, unit: 'second', tier: 'pro' },     // API $0.112 = 33.6cr → 52cr (35% margin)
  // Kling 3.0 Pro: $0.112/sec (audio off), $0.14/sec (audio on)
  'kling-3.0-pro': { base720p: 52, unit: 'second', tier: 'pro' },                // API $0.112 = 33.6cr → 52cr (35% margin)
  'kling-3.0-pro-audio': { base720p: 65, unit: 'second', tier: 'pro' },          // API $0.14 = 42cr → 65cr (35% margin)
  // Wan 2.2: $0.08/sec at 720p
  'wan-2.2': { base720p: 37, unit: 'second', tier: 'pro' },                      // API $0.08 = 24cr cost → 37cr (35% margin)
  // Wan 2.6: $0.10/sec at 720p, $0.15/sec at 1080p
  'wan-2.6': { base720p: 46, unit: 'second', tier: 'pro' },                      // API $0.10 = 30cr cost → 46cr (35% margin)
  // Wan 2.7: $0.10/sec at 720p, $0.15/sec at 1080p — TIERED, same curve as 2.6.
  // NOT flat. The "$0.10/sec flat" claim that used to sit here came from the
  // prose on https://fal.ai/wan-2.7 ("All endpoints are priced at $0.10 per
  // second... A 10-second 1080p video costs $1.00"), which the structured
  // `pricingInfoOverride` field on that same page contradicts. Read the field:
  //   curl -s https://fal.ai/models/fal-ai/wan/v2.7/text-to-video | tr -d '\\' \
  //     | grep -o '"pricingInfoOverride":"[^"]*"'
  // flatRate was making this bill 46 cr/s at 1080p against a $0.15/sec cost —
  // a 2.08% margin on the node's default model. Migration 133 clears the DB
  // flag; the generic 1.5x ladder rung is correct here (verified, not assumed:
  // 0.15/0.10 = 1.50 exactly), so no explicit rate table is needed.
  'wan-2.7': { base720p: 46, unit: 'second', tier: 'pro' },                      // API $0.10 = 30cr cost → 46cr (35% margin)
  // Wan Animate: Per-second pricing based on resolution (matches DB after migration 029)
  // 480p: 24 credits/sec, 580p: 32 credits/sec (base), 720p: 40 credits/sec
  // NOTE: Use calculateWanAnimateCreditCost() for accurate pricing with custom multipliers
  'wan-animate-replace': { base720p: 46, unit: 'second', tier: 'pro' },         // Repriced: ~35% margin at 720p
  'wan-animate-move': { base720p: 46, unit: 'second', tier: 'pro' },             // Repriced: ~35% margin at 720p
  // LTX-2 Pro: $0.06/sec
  'ltx-2': { base720p: 28, unit: 'second', tier: 'pro' },                        // API $0.06 = 18cr cost → 28cr (36% margin)
  'ltx-2-pro': { base720p: 28, unit: 'second', tier: 'pro' },                   // API $0.06 = 18cr cost → 28cr (36% margin)
  // LTX-2 Fast: ~$0.03/sec (estimated from Pro pricing)
  'ltx-2-fast': { base720p: 14, unit: 'second', tier: 'fast' },                 // API $0.03 = 9cr cost → 14cr (36% margin)
  // Minimax Hailuo: $0.045/sec at 768p
  'minimax-hailuo': { base720p: 21, unit: 'second', tier: 'base' },              // API $0.045 = 13.5cr cost → 21cr (36% margin)
  // MiniMax Hailuo 2.3 (Oct 2025): ~$0.047/sec @ 6s, ~$0.056/sec @ 10s at 768p
  'hailuo-2.3': { base720p: 22, unit: 'second', tier: 'base' },                  // API ~$0.047 = 14.1cr cost → 22cr (36% margin)
  // PixVerse 5.5: $0.04/sec at 720p (billed per video: $0.20/5s)
  'pixverse-5.5': { base720p: 18, unit: 'second', tier: 'base' },               // API $0.04 = 12cr cost → 18cr (33% margin)
  // PixVerse v6: $0.045/sec at 720p, $0.090/sec at 1080p (2x — standard 1.5x mult is close enough)
  'pixverse-6': { base720p: 21, unit: 'second', tier: 'base' },                  // API $0.045 = 13.5cr cost → 21cr (36% margin); 1080p tier mult ≈ 32cr (vs actual 42cr ideal)
  // Hunyuan 1.5: $0.075/sec — 480p is the ONLY resolution this model has, so
  // 35 cr/s already IS the 480p rate and no multiplier applies. The clip length
  // is fixed at 121 frames (~5s), so calculateVideoCreditCost() pins the units
  // to HUNYUAN_15_FIXED_SECONDS instead of billing the requested duration.
  'hunyuan-1.5': { base720p: 35, unit: 'second', tier: 'pro' },                  // API $0.075 = 22.5cr cost → 35cr (36% margin)
  // Kling Avatar: $0.0562/sec — Kling AI Avatar v2 Standard
  // (fal-ai/kling-video/ai-avatar/v2/standard). Was 32 cr/s, calibrated on the
  // $0.07/s v1 lipsync endpoint that no longer exists (404). Repriced in
  // migration 121: ceil($0.0562 × 300 / 0.65) = 26.
  'kling-avatar': { base720p: 26, unit: 'second', tier: 'pro' },                 // API $0.0562 = 16.9cr cost → 26cr (35% margin)
  // Grok Imagine Video (direct xAI API) — FLAT $0.050/sec at every resolution
  // (https://docs.x.ai/developers/models/grok-imagine-video). xAI collapsed the
  // old $0.07@720p / $0.05@480p split into one rate, so the 0.75× ladder must
  // no longer apply. Repriced by migration 118 — see that header for why the
  // rate is 21 and not the 24 the 35% formula gives.
  'grok-video': { base720p: 21, unit: 'second', tier: 'pro', flatRate: true },   // FLAT $0.050 = 15cr cost → 21cr (28.6% margin)
  'grok-imagine-video': { base720p: 21, unit: 'second', tier: 'pro', flatRate: true }, // Alias for selectionCostUtils when node.data.selectedModel uses the API name
  // Grok Imagine Video 1.5 (xAI; the -preview id is kept as the pricing key).
  // FLAT $0.080/sec at every resolution
  // (https://docs.x.ai/developers/models/grok-imagine-video-1.5) — the old
  // per-resolution 32/56 split is gone. Repriced by migration 118.
  'grok-imagine-video-1.5-preview': { base720p: 32, unit: 'second', tier: 'pro', flatRate: true }, // FLAT $0.080 = 24cr cost → 32cr (25% margin)
  // Seedance 2.0 (ByteDance via fal.ai — migrated off PiAPI in migration 113).
  // base720p is the 720p baseline; 480p is NOT base720p × 0.75 (the real ratio
  // is 0.466), so the number actually charged comes from the explicit
  // SEEDANCE_FAL_RATES branch below. No flatRate — resolution matters now.
  'seedance-2': { base720p: 141, unit: 'second', tier: 'pro' },                   // fal $0.3034 = 91cr cost → 141cr (35% margin)
  'seedance-2-fast': { base720p: 112, unit: 'second', tier: 'fast' },             // fal $0.2419 = 73cr cost → 112cr (35% margin)
  'seedance-2-mini': { base720p: 72, unit: 'second', tier: 'base' },              // fal $0.1547 = 46cr cost → 72cr (35% margin)
  // Seedance 2.5 (ByteDance via fal.ai): $0.4730/s @720p, $0.2205/s @480p.
  // base720p is the 720p baseline; 480p is NOT base720p × 0.75 (the real ratio
  // is 0.466), so the number actually charged comes from the explicit
  // SEEDANCE_FAL_RATES branch below. No flatRate — resolution matters here.
  'seedance-2.5': { base720p: 219, unit: 'second', tier: 'pro' },                 // API $0.4730 = 142cr cost → 219cr (35% margin)
  // LTX-2.3 Pro — T2V is 33% more expensive than I2V per fal.ai (Nov 2025 split):
  //   T2V and I2V both: $0.08/s @1080p, $0.16/s @1440p, $0.32/s @2160p
  // The modes cost the SAME (migration 129); the i2v column used to carry the
  // Fast tier's $0.06/$0.12/$0.24 and undercharged ~25% at every tier.
  //
  // All three entries are the DEFAULT-tier (1080p) scalar and are UNREACHABLE
  // for pricing: calculateLtx23ProCreditCost() short-circuits every path before
  // the MODEL_BASE_COSTS lookup. They are flatRate so that a surface which does
  // reach the table cannot apply the resolution ladder on top — that ladder is
  // exactly what made this function quote 42 at 1080p while the server billed
  // 28, the divergence CLAUDE.md records for this model.
  'ltx-2.3': { base720p: 37, unit: 'second', tier: 'pro', flatRate: true },      // $0.08 = 24cr cost → 37cr (35% margin)
  'ltx-2.3-pro': { base720p: 37, unit: 'second', tier: 'pro', flatRate: true },  // Alias
  'ltx-2.3-t2v': { base720p: 37, unit: 'second', tier: 'pro', flatRate: true },  // T2V SKU, same price since 129
  // LTX-2.3 Fast. This entry is the DEFAULT-tier (1080p) scalar and is
  // UNREACHABLE for pricing: calculateLtx23FastCreditCost() short-circuits every
  // path before the MODEL_BASE_COSTS lookup (migration 128). It is left at the
  // real 1080p rate so a catalogue surface reading this table shows what the
  // node quotes. Its previous value of 18 was a live divergence — the toolbar
  // multiplied it by the resolution ladder and quoted 27 at 1080p while the
  // server billed 19. Real per-tier rates live in LTX_23_FAST_RATES.
  'ltx-2.3-fast': { base720p: 28, unit: 'second', tier: 'fast', flatRate: true }, // 1080p $0.06 = 18cr cost → 28cr (35% margin)
  // MiniMax H3 (Hailuo 03) via fal.ai — FOUR resolutions, mandatory native audio
  // (no toggle, no surcharge). Migration 112 shipped 120 cr/s off fal's
  // learn-page rate of $0.26/s; migration 118 corrects it to fal's MODEL page,
  // which is the API pricing source of record and lists $0.13/s at 2K. The
  // learn page still says $0.26 — see the 118 header for why the model page wins.
  // This entry is the DEFAULT-tier (2K) scalar and is UNREACHABLE for pricing:
  // calculateMinimaxH3CreditCost() short-circuits every path before the
  // MODEL_BASE_COSTS lookup. It survives so a catalogue surface reading this
  // table shows the same headline number the node quotes on creation.
  // Real per-tier rates live in MINIMAX_H3_RATES (migration 127).
  'minimax-h3': { base720p: 60, unit: 'second', tier: 'pro', flatRate: true },   // 2K $0.13 = 39cr cost → 60cr (35% margin)
  // Note: veo-3-falai is defined in the fal.ai VEO models section above
  
  // HeyGen Models (via fal.ai) - per second at fixed resolution (no resolution multiplier)
  // Credits/sec = ceil(api_price_per_sec * 300 / 0.65) for ~35% margin
  // Pricing verified May 2026 via fal.ai model pages (e.g. https://fal.ai/models/fal-ai/heygen/avatar5/digital-twin
  // and https://fal.ai/models/fal-ai/heygen/avatar4/digital-twin). HeyGen's *direct* public page
  // lists $0.0667/sec for Avatar V tier, but the actual fal.ai routed cost is $0.10/sec — fal.ai
  // surcharges over HeyGen's own pricing, so do NOT recalibrate against the HeyGen-direct rate.
  'heygen-digital-twin-v3': { base720p: 8, unit: 'second', tier: 'base' },   // fal.ai $0.0167/sec
  'heygen-digital-twin-v4': { base720p: 47, unit: 'second', tier: 'pro' },   // fal.ai $0.1000/sec (verified)
  'heygen-digital-twin-v5': { base720p: 47, unit: 'second', tier: 'pro' },   // fal.ai $0.1000/sec — Avatar V (175+ language lip-sync), endpoint fal-ai/heygen/avatar5/digital-twin
  'heygen-video-agent': { base720p: 16, unit: 'second', tier: 'base' },      // fal.ai $0.0333/sec
  'heygen-translate-speed': { base720p: 24, unit: 'second', tier: 'base' },  // fal.ai $0.0500/sec
  'heygen-translate-precision': { base720p: 47, unit: 'second', tier: 'pro' }, // fal.ai $0.1000/sec (verified)
  'heygen-image-to-video': { base720p: 47, unit: 'second', tier: 'pro' },    // fal.ai $0.1000/sec (verified)
  // HeyGen Lipsync (re-sync lips to replacement audio)
  'heygen-lipsync-speed': { base720p: 24, unit: 'second', tier: 'base' },    // fal.ai $0.0500/sec
  'heygen-lipsync-precision': { base720p: 47, unit: 'second', tier: 'pro' }, // fal.ai $0.1000/sec

  // ESRGAN Image Upscaling (fal.ai) - flat cost per image
  // fal.ai $0.00111/compute-sec, ~3-8sec per image → ~$0.005 avg → 5cr (35% margin)
  'esrgan': { base720p: 5, unit: 'generation', tier: 'base' },
  // VideoScale AI Enhance (fal.ai bytedance-upscaler) — credits per SECOND at
  // 1080p; 2K/4K scale by VIDEOSCALE_RESOLUTION_RATIOS, not the generic ladder.
  // Seeds MODEL_BASE_COSTS so calculateVideoScaleCreditCost() has a base before
  // DB pricing loads. Migration 123 makes the DB row agree with this.
  'bytedance-upscaler': { base720p: 5, unit: 'second', tier: 'base', flatRate: true },

  // Speech-to-Text (Whisper) - flat cost per transcription (up to 5 minutes)
  'whisper-1': { base720p: 2, unit: 'generation', tier: 'base' },
  
  // Text Models (per generation) - fixed cost
  // (Kimi entries live in the Ollama Cloud block below.)
  // Tiered GPT pricing scale (rolled out 2026-06; real OpenAI spread is ~150× from nano to pro
  // so a flat fee misprices the high tiers heavily):
  //   nano / mini   → 1 cr/gen  (e.g. gpt-5-nano, gpt-5-mini, gpt-5.4-nano, gpt-4o-mini)
  //   standard      → 3 cr/gen  (e.g. gpt-5, gpt-5.2, gpt-5.4, gpt-4o, gpt-4, gpt-4-turbo)
  //   gpt-5.5       → 5 cr/gen  (mid-tier flagship)
  //   pro           → 15 cr/gen (e.g. gpt-5.4-pro, gpt-5.5-pro)
  'gemini-2.0-flash': { base720p: 3, unit: 'generation', tier: 'base' },
  // Google Gemini Text models — per-call estimate at ~500 input + 500 output tokens
  // Costs per million tokens: 3.5 Flash $1.50/$9 | 3.1 Pro $2/$12 | 3 Flash $0.50/$3 | 3.1 Flash-Lite $0.25/$1.50
  // At 1K tok avg per call: 3.5 Flash $0.00525 → 4cr | 3.1 Pro $0.007 → 5cr | 3 Flash $0.00175 → 2cr | 3.1 Flash-Lite $0.000875 → 1cr
  // Targeting ~50% margin: 4cr / 5cr / 2cr / 1cr
  'gemini-3.5-flash':       { base720p: 4, unit: 'generation', tier: 'fast' },
  'gemini-3.1-pro-preview': { base720p: 5, unit: 'generation', tier: 'pro' },
  'gemini-3-flash-preview': { base720p: 2, unit: 'generation', tier: 'fast' },
  'gemini-3.1-flash-lite':  { base720p: 1, unit: 'generation', tier: 'base' },
  // Gemini 3.6 Flash — $1.50 in / $7.50 out: same input price as 3.5 Flash and
  // CHEAPER output ($7.50 vs $9.00), so 4 cr carries a slightly better margin.
  'gemini-3.6-flash':       { base720p: 4, unit: 'generation', tier: 'fast' },
  // Gemini 3.5 Flash-Lite — $0.30 in / $2.50 out ≈ 0.84 cr cost at a 1K-token
  // call → 2 cr (~58% margin), one step above the cheaper 3.1 Flash-Lite.
  'gemini-3.5-flash-lite':  { base720p: 2, unit: 'generation', tier: 'base' },
  'gpt-4': { base720p: 3, unit: 'generation', tier: 'base' },
  'gpt-4-turbo': { base720p: 3, unit: 'generation', tier: 'base' },
  'gpt-3.5-turbo': { base720p: 1, unit: 'generation', tier: 'base' },
  'gpt-4o': { base720p: 3, unit: 'generation', tier: 'base' },
  'gpt-4o-mini': { base720p: 1, unit: 'generation', tier: 'base' },
  // GPT-5 series (tiered)
  'gpt-5-nano': { base720p: 1, unit: 'generation', tier: 'base' },
  'gpt-5-mini': { base720p: 1, unit: 'generation', tier: 'base' },
  'gpt-5.4-nano': { base720p: 1, unit: 'generation', tier: 'base' },
  'gpt-5': { base720p: 3, unit: 'generation', tier: 'base' },
  'gpt-5.2': { base720p: 3, unit: 'generation', tier: 'base' },
  'gpt-5.4': { base720p: 3, unit: 'generation', tier: 'base' },
  'gpt-5.4-pro': { base720p: 15, unit: 'generation', tier: 'pro' },
  // GPT-5.5 series (April 2026) — $5/$30 per MTok (5.5), $30/$180 (5.5-pro)
  'gpt-5.5': { base720p: 5, unit: 'generation', tier: 'base' },
  'gpt-5.5-pro': { base720p: 15, unit: 'generation', tier: 'pro' },
  // GPT-5.4 Mini — $0.75/$4.50 per MTok, mini tier
  'gpt-5.4-mini': { base720p: 1, unit: 'generation', tier: 'base' },
  // GPT-5.6 series (July 2026) — priced onto the same tier ladder as above:
  //   Sol   $5.00/$30 per MTok → 5 cr  (same tier as gpt-5.5)
  //   Terra $2.50/$15 per MTok → 3 cr  (same tier as gpt-5.4)
  //   Luna  $1.00/$6   per MTok → 2 cr (between mini and standard)
  'gpt-5.6-sol': { base720p: 5, unit: 'generation', tier: 'base' },
  'gpt-5.6-terra': { base720p: 3, unit: 'generation', tier: 'base' },
  'gpt-5.6-luna': { base720p: 2, unit: 'generation', tier: 'fast' },
  // xAI Grok text models (Aug 2026). Priced with the same 500-in/500-out
  // estimate the Gemini block above uses, then placed on the tier ladder:
  //   4.5     $2.00/$6.00 per MTok → $0.0040/call ≈ 1.2 cr cost → 3 cr
  //   4.3     $1.25/$2.50 per MTok → $0.0019/call ≈ 0.6 cr cost → 2 cr
  //   Build   $1.00/$2.00 per MTok → $0.0015/call ≈ 0.5 cr cost → 1 cr
  // Long-context calls (>200 k tokens) cost xAI 2× on every axis, but so do
  // the Gemini/GPT entries above; the flat-per-generation scheme absorbs that
  // the same way it does for every other text model.
  'grok-4.5': { base720p: 3, unit: 'generation', tier: 'base' },
  'grok-4.3': { base720p: 2, unit: 'generation', tier: 'fast' },
  'grok-build-0.1': { base720p: 1, unit: 'generation', tier: 'base' },
  // Claude models
  'claude-haiku-4-5': { base720p: 2, unit: 'generation', tier: 'base' },
  'claude-haiku-3-5': { base720p: 2, unit: 'generation', tier: 'base' },
  'claude-sonnet-4': { base720p: 5, unit: 'generation', tier: 'base' },
  'claude-sonnet-4-5': { base720p: 5, unit: 'generation', tier: 'base' },
  'claude-sonnet-4-6': { base720p: 5, unit: 'generation', tier: 'base' },
  'claude-opus-4': { base720p: 15, unit: 'generation', tier: 'pro' },
  'claude-opus-4-6': { base720p: 15, unit: 'generation', tier: 'pro' },
  // Opus 4.7 (April 2026) and 4.8 (May 2026) — same $5/$25 MTok as 4.6 → 15 cr/gen
  'claude-opus-4-7': { base720p: 15, unit: 'generation', tier: 'pro' },
  'claude-opus-4-8': { base720p: 15, unit: 'generation', tier: 'pro' },
  // Claude Opus 5 — $5/$25 per MTok, identical to Opus 4.8 → same 15 cr.
  'claude-opus-5': { base720p: 15, unit: 'generation', tier: 'pro' },
  // Claude Sonnet 5 — $3/$15 per MTok, identical to Sonnet 4.6 → same 5 cr.
  'claude-sonnet-5': { base720p: 5, unit: 'generation', tier: 'base' },
  // Ollama Cloud models (Kimi / Qwen / MiniMax) — priced at half of Claude Sonnet (5 cr) → 3 cr/call.
  'kimi-k2.6': { base720p: 3, unit: 'generation', tier: 'base' },
  'kimi-k2.5': { base720p: 3, unit: 'generation', tier: 'base' },
  'kimi-k2-instruct': { base720p: 3, unit: 'generation', tier: 'base' },
  'qwen-3.5': { base720p: 3, unit: 'generation', tier: 'base' },
  'qwen-3.5-397b': { base720p: 3, unit: 'generation', tier: 'base' },
  'qwen-3-vl-235b': { base720p: 3, unit: 'generation', tier: 'base' },
  'qwen-3-coder-next': { base720p: 3, unit: 'generation', tier: 'base' },
  // Legacy NIM-era aliases — still recognised, half-Sonnet pricing
  'qwen-3.5-122b': { base720p: 3, unit: 'generation', tier: 'base' },
  'qwen-3-next-80b': { base720p: 3, unit: 'generation', tier: 'base' },
  'qwen-2.5-7b': { base720p: 3, unit: 'generation', tier: 'base' },
  'minimax-m2.7': { base720p: 3, unit: 'generation', tier: 'base' },
  'minimax-m2.5': { base720p: 3, unit: 'generation', tier: 'base' },
  'minimax-m2.1': { base720p: 3, unit: 'generation', tier: 'base' },
  'minimax-m2': { base720p: 3, unit: 'generation', tier: 'base' },

  // ── ElevenLabs audio (the first audio-output generators) ──
  // Approved 35%-margin pricing (1 credit = $0.00333; credits ≈ USD × 462).
  // TTS is CHARACTER-based — the base720p values below are credits per 1,000
  // characters (used for display fallbacks; the send-button cost is computed
  // per-prompt-length via calculateElevenLabsTTSCreditCost). SFX/Music are flat
  // per-generation (the cost fn is NOT given a duration), priced at a sensible
  // default length: SFX 10s × 1 cr/s = 10; Music 30s × 1.2 cr/s = 36 (30s is the
  // node's default length). Mirrors server CreditService FALLBACK_MODEL_COSTS.
  'eleven_v3': { base720p: 46, unit: 'generation', tier: 'pro' },               // $0.10/1k chars → 46 cr/1k
  'eleven_multilingual_v2': { base720p: 46, unit: 'generation', tier: 'base' }, // $0.10/1k chars → 46 cr/1k
  'eleven_flash_v2_5': { base720p: 23, unit: 'generation', tier: 'fast' },      // $0.05/1k chars → 23 cr/1k
  'eleven_turbo_v2_5': { base720p: 23, unit: 'generation', tier: 'fast' },      // $0.05/1k chars → 23 cr/1k
  'eleven_text_to_sound_v2': { base720p: 10, unit: 'generation', tier: 'base' },// $0.12/min → 1 cr/s; 10s default → 10 cr/gen
  'music_v1': { base720p: 36, unit: 'generation', tier: 'base' },               // $0.15/min → 1.2 cr/s; 30s default → 36 cr/gen
  'music_v2': { base720p: 36, unit: 'generation', tier: 'base' },
};

// Seed MODEL_BASE_COSTS with fallbacks so the UI works before API loads
Object.assign(MODEL_BASE_COSTS, FALLBACK_COSTS);

// Legacy flat cost registry for backward compatibility
// Re-derived dynamically so it reflects DB-loaded pricing
export function getModelCreditCosts(): Record<string, { creditsPerUnit: number; unit: 'generation' | 'second' }> {
  return Object.fromEntries(
    Object.entries(MODEL_BASE_COSTS).map(([key, val]) => [
      key,
      { creditsPerUnit: val.base720p, unit: val.unit }
    ])
  );
}

/** @deprecated Use getModelCreditCosts() instead */
export const MODEL_CREDIT_COSTS: Record<string, { creditsPerUnit: number; unit: 'generation' | 'second' }> =
  Object.fromEntries(
    Object.entries(FALLBACK_COSTS).map(([key, val]) => [
      key,
      { creditsPerUnit: val.base720p, unit: val.unit }
    ])
  );

/**
 * Convert resolution string to tier
 */
export function getResolutionTier(resolution: string): ResolutionTier {
  // Special resolutions for Wan Animate (must check before general video resolutions)
  if (resolution === '480p' || resolution.includes('480')) {
    return '480p';
  }
  if (resolution === '580p' || resolution.includes('580')) {
    return '580p';
  }
  
  // Video resolutions
  if (resolution === '720p' || resolution.includes('720') || resolution.includes('1280x720') || resolution.includes('720x1280')) {
    return '720p';
  }
  if (resolution === '1080p' || resolution.includes('1080') || resolution.includes('1920x1080') || resolution.includes('1080x1920')) {
    return '1080p';
  }
  if (resolution === '1440p' || resolution.includes('1440') || resolution.includes('2560x1440') || resolution.includes('1440x2560') || resolution.includes('1792x1024') || resolution.includes('1024x1792')) {
    return '1080p'; // 1792x1024 is roughly 1080p equivalent
  }
  if (resolution === '4k' || resolution.includes('4k') || resolution.includes('4K') || resolution.includes('3840') || resolution.includes('2160')) {
    return '4k';
  }
  
  // Image resolutions (by pixel size)
  if (resolution.includes('512') || resolution === '512px') {
    return '720p'; // Base tier
  }
  if (resolution.includes('1024') || resolution === '1024px') {
    return '1080p';
  }
  if (resolution.includes('2048') || resolution === '2048px') {
    return '1440p';
  }
  if (resolution.includes('4096') || resolution === '4096px') {
    return '4k';
  }
  
  // Default to base resolution
  return '720p';
}

/**
 * Calculate credit cost with resolution
 * @param modelId - The model identifier
 * @param units - Number of units (seconds for video, 1 for generation-based)
 * @param resolution - Resolution tier or string
 * @returns Total credit cost
 */
export function calculateCreditCostWithResolution(
  modelId: string,
  units: number = 1,
  resolution: ResolutionTier | string = '720p'
): number {
  // Grok video — xAI now charges ONE flat per-second rate per model, so the
  // 0.75× / 1× ladder must not apply. Routed early so callers using
  // calculateCreditCostWithResolution() directly (e.g. selectionCostUtils)
  // get the correct number too.
  const grokVideoCost = calculateGrokVideoCreditCost(modelId, units);
  if (grokVideoCost !== undefined) return grokVideoCost;

  // VEO 3.1 Standard — explicit non-linear table, see the function's comment.
  const veoStdCost = calculateVeo31StandardCreditCost(modelId, units, resolution);
  if (veoStdCost !== undefined) return veoStdCost;

  // VEO 3.1 Fast — same reason as Standard: Google's curve is not the ladder.
  const veoFastCost = calculateVeo31FastCreditCost(modelId, units, resolution);
  if (veoFastCost !== undefined) return veoFastCost;

  // VEO 3.1 Lite and fal's VEO 3.1 - both have non-ladder curves; see helpers.
  const veoLiteCost = calculateVeo31LiteCreditCost(modelId, units, resolution);
  if (veoLiteCost !== undefined) return veoLiteCost;
  const falVeoCost = calculateFalVeo31CreditCost(modelId, units, resolution);
  if (falVeoCost !== undefined) return falVeoCost;

  // Kling Image O3 — fal prices 1K and 2K identically and doubles at 4K, which
  // the multiplier ladder below cannot express (it would bill 4K at 3×, i.e. 39
  // instead of 26). Routed early for the same reason as the Grok branch above:
  // selectionCostUtils calls THIS function directly, not calculateImageCreditCost,
  // so a branch placed only in the latter leaves the multi-select toolbar and the
  // whole-flow estimate quoting a different number than the Send button.
  if (modelId === 'kling-image-o3') {
    return calculateKlingImageCreditCost(modelId, String(resolution)) * Math.ceil(units);
  }

  // Grok Image 2.0 — explicit tier table, never the ladder. This signature
  // carries no quality or input-image count, so a caller that only has a
  // resolution gets the LOW tier at that resolution; the node badge and
  // selectionCostUtils call calculateGrokImage2CreditCost() with all four.
  if (modelId === GROK_IMAGE_2_MODEL_ID) {
    return calculateGrokImage2CreditCost(modelId, undefined, String(resolution)) * Math.ceil(units);
  }

  // Seedance 2.x (fal.ai) — explicit per-resolution rates. Routed early for the
  // same reason as the two branches above: selectionCostUtils calls THIS
  // function directly, so a branch placed only in calculateVideoCreditCost()
  // would leave the multi-select toolbar and the whole-flow estimate quoting a
  // different number than the Send button.
  const seedanceCost = calculateSeedanceCreditCost(modelId, units, resolution);
  if (seedanceCost !== undefined) return seedanceCost;

  // MiniMax H3 — same reason again, and it is now a LIVE bypass rather than the
  // latent one it used to be. While H3 exposed only "2K" the number was right by
  // luck: getResolutionTier() does not recognise "2K", fell through to the
  // '720p' default, and that multiplier happens to be 1. Migration 127 exposed
  // all four of fal's tiers at four different rates, so `resolution` must reach
  // the helper — this is the call the multi-select toolbar and the whole-flow
  // estimate make, and without it they would quote the 2K price for a 4K node.
  const minimaxH3Cost = calculateMinimaxH3CreditCost(modelId, units, resolution);
  if (minimaxH3Cost !== undefined) return minimaxH3Cost;

  // LTX-2.3 Fast — see calculateLtx23FastCreditCost(). This branch did NOT
  // exist before migration 128, so this function priced the model off
  // MODEL_BASE_COSTS (18) times the resolution ladder: 27 at 1080p against a
  // server charge of 19. The multi-select toolbar and whole-flow estimate have
  // been quoting a number the Send button never charged.
  const ltx23FastCost = calculateLtx23FastCreditCost(modelId, units, String(resolution));
  if (ltx23FastCost !== undefined) return ltx23FastCost;

  // LTX-2.3 Pro — see calculateLtx23ProCreditCost(). This branch did not exist
  // before migration 129 either, so this function priced the model off
  // MODEL_BASE_COSTS (28) x the ladder and quoted 42 at 1080p against a server
  // charge of 28 — the divergence CLAUDE.md names for this exact model.
  const ltx23ProCost = calculateLtx23ProCreditCost(modelId, units, String(resolution));
  if (ltx23ProCost !== undefined) return ltx23ProCost;

  // Hunyuan 1.5 — same reason again, and here the bypass would be LIVE: the
  // generic tail below multiplies by `units`, and Hunyuan's clip length is
  // fixed, so the toolbar would quote a duration-scaled number the Send button
  // never charges.
  const hunyuanCost = calculateHunyuanCreditCost(modelId);
  if (hunyuanCost !== undefined) return hunyuanCost;

  const tier = typeof resolution === 'string' && ['720p', '1080p', '1440p', '4k'].includes(resolution)
    ? resolution as ResolutionTier
    : getResolutionTier(resolution);

  const multiplier = RESOLUTION_MULTIPLIERS[tier];
  const modelCost = MODEL_BASE_COSTS[modelId];
  
  if (!modelCost) {
    // Try to infer from model name patterns
    let baseCost = 0;
    if (modelId.includes('veo-3') || modelId.includes('veo-3.1')) {
      const isLite = modelId.includes('lite');
      const isFast = modelId.includes('fast');
      baseCost = isLite ? 24 : isFast ? 40 : 120;
      return Math.ceil(baseCost * multiplier * Math.ceil(units));
    }
    if (modelId.includes('imagen') || modelId.includes('gpt-image')) {
      return Math.ceil(5 * multiplier);
    }
    if (modelId.includes('nano-banana')) {
      const isPro = modelId.includes('pro');
      baseCost = isPro ? 10 : 5;
      return Math.ceil(baseCost * multiplier);
    }
    return 0;
  }
  
  // Flat-rate models charge the same per unit regardless of resolution
  const effectiveMultiplier = modelCost.flatRate ? 1 : multiplier;

  if (modelCost.unit === 'second') {
    return Math.ceil(modelCost.base720p * effectiveMultiplier * Math.ceil(units));
  }

  return Math.ceil(modelCost.base720p * effectiveMultiplier);
}

/**
 * Calculate credit cost for a generation (legacy, uses 720p as default)
 * @param modelId - The model identifier
 * @param units - Number of units (seconds for video, 1 for generation-based)
 * @returns Total credit cost, or undefined if model not found
 */
export function calculateCreditCost(modelId: string, units: number = 1): number | undefined {
  return calculateCreditCostWithResolution(modelId, units, '720p') || undefined;
}

/**
 * Calculate video generation credit cost with resolution
 * @param modelId - Video model ID
 * @param durationSeconds - Video duration in seconds
 * @param resolution - Resolution tier or string
 * @returns Credit cost for the video
 */
export function calculateVideoCreditCost(
  modelId: string,
  durationSeconds: number | string,
  resolution: ResolutionTier | string = '720p'
): number {
  const duration = typeof durationSeconds === 'string' ? parseFloat(durationSeconds) : durationSeconds;

  // Grok video — see calculateGrokVideoCreditCost(). xAI charges ONE flat
  // per-second rate per model, so there is no resolution term at all.
  // Must mirror CreditService.calculateCost() in server.
  const grokVideoCost = calculateGrokVideoCreditCost(modelId, duration);
  if (grokVideoCost !== undefined) return grokVideoCost;

  // VEO 3.1 Standard — explicit non-linear table, see the function's comment.
  const veoStdCost = calculateVeo31StandardCreditCost(modelId, duration, resolution);
  if (veoStdCost !== undefined) return veoStdCost;

  // VEO 3.1 Fast — same reason as Standard: Google's curve is not the ladder.
  const veoFastCost = calculateVeo31FastCreditCost(modelId, duration, resolution);
  if (veoFastCost !== undefined) return veoFastCost;

  // VEO 3.1 Lite and fal's VEO 3.1 - both have non-ladder curves; see helpers.
  const veoLiteCost = calculateVeo31LiteCreditCost(modelId, duration, resolution);
  if (veoLiteCost !== undefined) return veoLiteCost;
  const falVeoCost = calculateFalVeo31CreditCost(modelId, duration, resolution);
  if (falVeoCost !== undefined) return falVeoCost;

  // Seedance 2.x — see calculateSeedanceCreditCost(). Mirrors the server branch
  // in CreditService.calculateCost() exactly; if the server has a branch and the
  // client doesn't, the badge and the bill diverge.
  const seedanceCost = calculateSeedanceCreditCost(modelId, duration, resolution);
  if (seedanceCost !== undefined) return seedanceCost;

  // MiniMax H3 — see calculateMinimaxH3CreditCost(). Same early-route rationale
  // as Seedance above. This is the Send-button / node-badge path; the toolbar
  // path is the identical call inside calculateCreditCostWithResolution(). Both
  // must pass `resolution` or the two quote different numbers above 2K.
  const minimaxH3Cost = calculateMinimaxH3CreditCost(modelId, duration, resolution);
  if (minimaxH3Cost !== undefined) return minimaxH3Cost;

  // LTX-2.3 Fast — three tiers plus the >10s downgrade. Same early-route
  // rationale; this is the Send-button / badge path, the one above is the
  // toolbar path, and both must reach the same table.
  const ltx23FastCost = calculateLtx23FastCreditCost(modelId, duration, String(resolution));
  if (ltx23FastCost !== undefined) return ltx23FastCost;

  // LTX-2.3 Pro — same early-route rationale; this is the Send-button / badge
  // path, the one above is the toolbar path, and both must reach one table.
  const ltx23ProCost = calculateLtx23ProCreditCost(modelId, duration, String(resolution));
  if (ltx23ProCost !== undefined) return ltx23ProCost;

  // Hunyuan 1.5 — see calculateHunyuanCreditCost(). Same early-route rationale
  // as the branches above.
  const hunyuanCost = calculateHunyuanCreditCost(modelId);
  if (hunyuanCost !== undefined) return hunyuanCost;

  return calculateCreditCostWithResolution(modelId, duration, resolution);
}

/**
 * Seedance 2.x (fal.ai) per-second credit rates, by resolution.
 *
 * fal bills these models on OUTPUT PIXEL TOKENS, so the 480p rate is ~0.466× of
 * 720p — not the 0.75× the generic RESOLUTION_MULTIPLIERS ladder would apply.
 * Every tier therefore needs an explicit table on BOTH sides of the wire.
 * Mirror of SEEDANCE_FAL_RATES in server/services/CreditService.ts.
 *
 * credits = ceil(usd_per_sec × 300 / 0.65)  (≈ ×461.538, ~35% gross margin;
 * the ×450 factor in the node-creation guide is IMAGE-only and is wrong here)
 * fal prices verified 2026-08-12 via https://fal.ai/api/models?keywords=seedance
 *   seedance-2.5      : $0.4730/s @720p, $0.2205/s @480p
 *   seedance-2        : $0.3034/s @720p  (fal's 2.0 "standard" tier)
 *   seedance-2-fast   : $0.2419/s @720p
 *   seedance-2-mini   : $0.1547/s @720p, $0.0721/s @480p
 *
 * fal publishes no 480p per-second figure for the 2.0 standard and fast tiers,
 * only a flat token rate. The token formula is linear in output pixel area, and
 * the two tiers that DO publish both numbers agree on the ratio to five decimal
 * places (2.5: 0.2205/0.4730 = 0.46617; mini: 0.0721/0.1547 = 0.46606), so the
 * 480p rate for standard and fast is that same ratio applied to their 720p rate:
 *   standard 480p = 0.3034 × 0.46617 = $0.14144/s → ceil(65.28) = 66
 *   fast     480p = 0.2419 × 0.46617 = $0.11277/s → ceil(52.05) = 53
 */
const SEEDANCE_FAL_RATES: Record<string, Record<'480p' | '720p', number>> = {
  'seedance-2.5': { '480p': 102, '720p': 219 },
  'seedance-2': { '480p': 66, '720p': 141 },
  'seedance-2-fast': { '480p': 53, '720p': 112 },
  'seedance-2-mini': { '480p': 34, '720p': 72 },
};

/**
 * Credit cost for a Seedance 2.x generation, or undefined if the model is not
 * a Seedance 2.x fal.ai SKU (so callers can fall through to the generic path).
 */
export function calculateSeedanceCreditCost(
  modelId: string,
  durationSeconds: number | string,
  resolution: ResolutionTier | string = '720p'
): number | undefined {
  const rates = SEEDANCE_FAL_RATES[modelId];
  if (!rates) return undefined;
  const tier = String(resolution || '720p').includes('480') ? '480p' : '720p';
  const duration = typeof durationSeconds === 'string' ? parseFloat(durationSeconds) : durationSeconds;
  return rates[tier] * Math.max(1, Math.ceil(Number(duration) || 1));
}

/**
 * VEO 3.1 Standard (Google) per-second credit rate.
 *
 * Google's curve is NOT the generic ladder:
 *   720p  $0.40/s -> 241 cr/s
 *   1080p $0.40/s -> 241 cr/s   (SAME price as 720p, not 1.5x)
 *   4K    $0.60/s -> 361 cr/s   (1.5x the 720p price, not 3x)
 * ceil(usd * 2 / 0.00333), the 50% premium-video margin used for Google rows.
 * Source: https://ai.google.dev/gemini-api/docs/pricing (verified 2026-09-01).
 *
 * Applying RESOLUTION_MULTIPLIERS to a 241 base would bill 362 at 1080p and 723
 * at 4K — a 50% and 100% overcharge. Repricing Standard without this branch
 * would have converted a negative margin into a large overcharge, so the branch
 * is not an optimisation, it is the point.
 *
 * BOTH ids are priced here on purpose. The Veo node's selectedModel is
 * 'veo-3.1-generate-preview', which is what the CLIENT estimates under, but
 * server/routes/veo.ts remaps it to 'veo-3-pro' before reserving, which is what
 * the SERVER bills under. If only one carried 241 the badge and the bill would
 * differ by 2x on every Standard generation.
 *
 * Mirror of the VEO_31_STANDARD_RATES branch in server/services/CreditService.ts.
 */
const VEO_31_STANDARD_RATES: Record<string, number> = {
  '720p': 241,
  '1080p': 241,
  '4k': 361,
};

const VEO_31_STANDARD_IDS = new Set(['veo-3.1-generate-preview', 'veo-3-pro']);

/**
 * Veo 3.1 (Google direct) resolution validity. Client MIRROR of
 * resolveVeo31Resolution in server/config/veoResolution.ts; the contract is
 * documented there. Keep the two identical.
 *   Standard / Fast : 720p, 1080p, 4k    Lite : 720p, 1080p (no 4k)
 *   absent -> 720p; trimmed + lowercased ('4K' -> '4k'); anything else (1440p,
 *   2160p, garbage) or an unknown model is an error; 1080p/4k require 8s when
 *   a duration is supplied.
 * The price functions below validate the TIER only. They return NaN for an
 * impossible tier so no quote is shown, instead of pricing a neighbouring tier
 * the server refuses. Callers that know the duration (the node badge) pass it
 * here to hide quotes for impossible length/resolution pairs too.
 */
export type Veo31Tier = '720p' | '1080p' | '4k';
const VEO_31_TIERS: Record<string, readonly Veo31Tier[]> = {
  'veo-3.1-generate-preview': ['720p', '1080p', '4k'],
  'veo-3-pro': ['720p', '1080p', '4k'],
  'veo-3.1-fast-generate-preview': ['720p', '1080p', '4k'],
  'veo-3-fast': ['720p', '1080p', '4k'],
  'veo-3.1-lite-generate-preview': ['720p', '1080p'],
};

export function isVeo31Model(modelId: string): boolean {
  return Object.prototype.hasOwnProperty.call(VEO_31_TIERS, modelId);
}

export function resolveVeo31Resolution(
  modelId: string,
  resolution?: string | null,
  durationSeconds?: number | string,
): { tier: Veo31Tier } | { error: string } {
  const allowed = isVeo31Model(modelId) ? VEO_31_TIERS[modelId] : undefined;
  if (!allowed) return { error: `${modelId} is not a supported Veo 3.1 model.` };
  const raw = resolution === undefined || resolution === null ? '' : String(resolution).trim().toLowerCase();
  const requested = raw === '' ? '720p' : raw;
  if (!(allowed as readonly string[]).includes(requested)) {
    return { error: `${modelId} renders at ${allowed.join(', ')} only, not "${resolution}".` };
  }
  const tier = requested as Veo31Tier;
  if (durationSeconds !== undefined && tier !== '720p' && Number(durationSeconds) !== 8) {
    return { error: `${modelId} renders ${tier} only at 8 seconds; ${durationSeconds}s was requested.` };
  }
  return { tier };
}

/** Tier to price, or undefined when the tier is impossible for the model. */
function veo31PricedTier(modelId: string, resolution?: string | null): Veo31Tier | undefined {
  const r = resolveVeo31Resolution(modelId, resolution);
  return 'error' in r ? undefined : r.tier;
}

/**
 * VEO 3.1 Lite (Google) per-second credit rate.
 *
 *   720p  $0.05/s -> 31 cr/s
 *   1080p $0.08/s -> 49 cr/s
 * ceil(usd * 2 / 0.00333), the 50% premium-video basis. Lite has NO 4K tier
 * (nodeDefinitions restricts its resolution dropdown to 720p/1080p).
 * Source: https://ai.google.dev/gemini-api/docs/pricing (verified 2026-09-01).
 *
 * Migration 124 raised Lite from the 35% basis (24 @720p, 36 @1080p via the
 * ladder) to the 50% basis every other Google row already used.
 *
 * An explicit table is required, not just a new base: the 1.5x ladder off a 31
 * base gives ceil(46.5) = 47 at 1080p, but Google's $0.08/s is 49. Same trap as
 * VEO 3.1 Standard in migration 120, where a bare base would have overcharged.
 *
 * Mirror of the VEO_31_LITE_RATES branch in server/services/CreditService.ts.
 */
const VEO_31_LITE_RATES: Record<string, number> = {
  '720p': 31,
  '1080p': 49,
};

export function calculateVeo31LiteCreditCost(
  modelId: string,
  durationSeconds: number | string,
  resolution: ResolutionTier | string = '720p'
): number | undefined {
  if (modelId !== 'veo-3.1-lite-generate-preview') return undefined;
  // Lite has no 4k tier. An impossible tier gets no quote (NaN). It used to be
  // quoted at the 1080p rate for a render the server now refuses.
  const tier = veo31PricedTier(modelId, resolution);
  if (!tier) return NaN;
  const duration = typeof durationSeconds === 'string' ? parseFloat(durationSeconds) : durationSeconds;
  return VEO_31_LITE_RATES[tier] * Math.max(1, Math.ceil(Number(duration) || 1));
}

/**
 * fal.ai VEO 3.1 Standard - priced by AUDIO STATE.
 *
 * fal charges (https://fal.ai/models/fal-ai/veo3.1, verified 2026-09-01):
 *   720p / 1080p: $0.20/s without audio, $0.40/s with audio
 *   4K:           $0.40/s without audio, $0.60/s with audio
 *
 * At the 50% basis, ceil(usd * 2 / 0.00333):
 *   audio OFF: 720p 120*, 1080p 120*, 4K 241
 *   audio ON : 720p 241,  1080p 241,  4K 361
 *   (*) policy is 121; we keep today's 120, which is 49.95% margin, rather than
 *       raise a tier by one credit for nothing. Migration 124.
 *
 * Note 720p and 1080p cost the SAME, and 4K is only 2x the base - so the generic
 * 1x/1.5x/3x ladder is wrong here in both directions, exactly as it was for the
 * Google Standard tier in migration 120.
 *
 * WHY TWO MODEL IDS RATHER THAN AN ARGUMENT. `-audio` SKUs are how this codebase
 * already carries an audio flag from the route into pricing: falai.ts remaps
 * kling-2.6 -> kling-2.6-audio before reserving, and kling-2.6-audio has been a
 * model_registry row since migration 005. veo-3.1 now follows the same path, so
 * the flag travels through existing plumbing rather than a new parameter that a
 * caller could forget to pass.
 *
 * THE FAST TIER HAS THE SAME SHAPE (migration 131). fal, same structured field
 * on fal-ai/veo3.1's Fast tier: "$0.10 without audio or $0.15 with audio for
 * 720p or 1080p. At 4k, $0.30 per second without audio, or $0.35 with."
 *   audio OFF: 720p 61,  1080p 61,  4K 181
 *   audio ON : 720p 91,  1080p 91,  4K 211
 * Until 131 the fast tier had no table at all: an 80 cr/s row with
 * flat_rate = false fell through the generic ladder to 80/120/240, which
 * overcharged at every tier but 720p-with-audio and ignored audio entirely.
 *
 * Mirror of the FAL_VEO_31_RATES branch in server/services/CreditService.ts.
 */
const FAL_VEO_31_RATES: Record<string, Record<string, number>> = {
  'veo-3.1':            { '720p': 120, '1080p': 120, '4k': 241 },
  'veo-3.1-audio':      { '720p': 241, '1080p': 241, '4k': 361 },
  'veo-3.1-fast':       { '720p':  61, '1080p':  61, '4k': 181 },
  'veo-3.1-fast-audio': { '720p':  91, '1080p':  91, '4k': 211 },
};

export function calculateFalVeo31CreditCost(
  modelId: string,
  durationSeconds: number | string,
  resolution: ResolutionTier | string = '720p'
): number | undefined {
  const table = FAL_VEO_31_RATES[modelId];
  if (!table) return undefined;
  const res = String(resolution || '720p');
  const tier = (res.includes('4k') || res.includes('4K') || res.includes('2160') || res.includes('3840'))
    ? '4k'
    : (res.includes('1080') || res.includes('1440')) ? '1080p' : '720p';
  const duration = typeof durationSeconds === 'string' ? parseFloat(durationSeconds) : durationSeconds;
  return table[tier] * Math.max(1, Math.ceil(Number(duration) || 1));
}

/**
 * Pricing id for fal VEO 3.1 given the node's audio toggle.
 *
 * UNKNOWN AUDIO BILLS AS AUDIO-ON, DELIBERATELY. Only an explicit `false`
 * selects the cheaper SKU. There is no client quote path for this model today
 * (no node offers fal's veo-3.1 - the Veo node is Google-only on both the UI and
 * the headless registry), so if one is ever added and forgets to thread the
 * flag, this defaults to the expensive assumption. Over-quoting is recoverable;
 * under-billing is the failure mode migration 124 exists to fix.
 *
 * Must match the remap in server/routes/falai.ts.
 *
 * `baseModelId` selects the tier: 'veo-3.1' (default, back-compatible) or
 * 'veo-3.1-fast'. Anything else is returned untouched — this function must not
 * invent a `-audio` SKU for a model that has no row.
 */
export function falVeo31PricingId(
  enableAudio?: boolean,
  baseModelId: string = 'veo-3.1',
): string {
  if (baseModelId !== 'veo-3.1' && baseModelId !== 'veo-3.1-fast') return baseModelId;
  return enableAudio === false ? baseModelId : `${baseModelId}-audio`;
}

/**
 * VEO 3.1 Fast (Google) per-second credit rate.
 *
 * Google's Fast curve, like Standard's, is NOT the generic ladder:
 *   720p  $0.10/s -> 60 cr/s   (policy 61; see below)
 *   1080p $0.12/s -> 73 cr/s   (the ladder billed 90 — a 23% overcharge)
 *   4K    $0.30/s -> 180 cr/s  (policy 181; see below)
 * Source: https://ai.google.dev/gemini-api/docs/pricing (verified 2026-09-01).
 *
 * The ladder took the 60 cr/s 720p base to 90 at 1080p and 180 at 4K. 4K lands
 * within a credit of correct by coincidence, but 1080p was a real overcharge:
 * Google only charges 20% more there ($0.10 -> $0.12) while the ladder adds 50%.
 *
 * WHY 60 AND 180 RATHER THAN THE POLICY 61 AND 181. Migration 123 is a
 * reduction-only change. ceil($0.10 * 2 / 0.00333) = 61 and
 * ceil($0.30 * 2 / 0.00333) = 181, but moving 720p 60 -> 61 and 4K 180 -> 181
 * would RAISE two tiers by a credit each. Both already sit at ~49.95% margin,
 * so the shortfall is pure rounding. Only the overcharge moves.
 *
 * Both ids are priced here for the same reason as Standard: server/routes/veo.ts
 * remaps 'veo-3.1-fast-generate-preview' onto 'veo-3-fast' before reserving,
 * while the client estimates under the UI id.
 *
 * Mirror of the VEO_31_FAST_RATES branch in server/services/CreditService.ts.
 */
const VEO_31_FAST_RATES: Record<string, number> = {
  '720p': 60,
  '1080p': 73,
  '4k': 180,
};

const VEO_31_FAST_IDS = new Set(['veo-3.1-fast-generate-preview', 'veo-3-fast']);

export function calculateVeo31FastCreditCost(
  modelId: string,
  durationSeconds: number | string,
  resolution: ResolutionTier | string = '720p'
): number | undefined {
  if (!VEO_31_FAST_IDS.has(modelId)) return undefined;
  const tier = veo31PricedTier(modelId, resolution);
  if (!tier) return NaN;
  const duration = typeof durationSeconds === 'string' ? parseFloat(durationSeconds) : durationSeconds;
  return VEO_31_FAST_RATES[tier] * Math.max(1, Math.ceil(Number(duration) || 1));
}

export function calculateVeo31StandardCreditCost(
  modelId: string,
  durationSeconds: number | string,
  resolution: ResolutionTier | string = '720p'
): number | undefined {
  if (!VEO_31_STANDARD_IDS.has(modelId)) return undefined;
  const tier = veo31PricedTier(modelId, resolution);
  if (!tier) return NaN;
  const duration = typeof durationSeconds === 'string' ? parseFloat(durationSeconds) : durationSeconds;
  return VEO_31_STANDARD_RATES[tier] * Math.max(1, Math.ceil(Number(duration) || 1));
}

/**
 * Grok video (xAI direct) per-second credit rates. Both models are FLAT: xAI
 * publishes a single per-second output price with no resolution term
 *   grok-imagine-video      $0.050/s → 21 cr/s
 *   grok-imagine-video-1.5  $0.080/s → 32 cr/s
 * (https://docs.x.ai/developers/models/grok-imagine-video and
 *  https://docs.x.ai/developers/models/grok-imagine-video-1.5).
 *
 * Resolution is deliberately NOT a parameter — same reasoning as
 * calculateMinimaxH3CreditCost() below. The Grok Video node still offers a
 * 480p/720p dropdown (and DEFAULTS to 480p), but that choice no longer changes
 * the price, so applying the 0.75× ladder would invent a discount the server
 * does not honour.
 *
 * Why an explicit branch rather than the `flatRate` flag alone: initializePricing()
 * rebuilds MODEL_BASE_COSTS from the /api/credits/pricing payload and DROPS
 * flatRate (its row type has no flat_rate field), so once DB pricing loads the
 * ladder would come back and the badge would quote 0.75× of what the server bills.
 *
 * Returns undefined for every other model so callers can early-route, exactly
 * like calculateSeedanceCreditCost() above.
 *
 * Mirror of the grok video branch in server/services/CreditService.ts.
 */
const GROK_VIDEO_RATES: Record<string, number> = {
  'grok-video': 21,
  'grok-imagine-video': 21,
  'grok-imagine-video-1.5-preview': 32,
  'grok-imagine-video-1.5': 32,
};

export function calculateGrokVideoCreditCost(
  modelId: string,
  durationSeconds: number | string
): number | undefined {
  const rate = GROK_VIDEO_RATES[modelId];
  if (rate === undefined) return undefined;
  const duration = typeof durationSeconds === 'string' ? parseFloat(durationSeconds) : durationSeconds;
  return rate * Math.max(1, Math.ceil(Number(duration) || 1));
}

/**
 * MiniMax H3 (fal.ai) per-second credit rates, by resolution.
 *
 * fal exposes FOUR tiers and prices each differently. Its model page, verbatim:
 * "Video costs $0.05 per second at 480p, $0.06 per second at 768p, $0.13 per
 * second at 2K and $0.16 per second at 4K."
 *
 * Rates via the fal-video formula ceil(usd × 300 / 0.65) (35% margin; the same
 * derivation migrations 111/112/118 used):
 *   480P  0.05 × 300 = 15 ; 15 / 0.65 = 23.08 -> 24
 *   768P  0.06 × 300 = 18 ; 18 / 0.65 = 27.69 -> 28
 *   2K    0.13 × 300 = 39 ; 39 / 0.65 = 60.00 -> 60   (unchanged, migration 118)
 *   4K    0.16 × 300 = 48 ; 48 / 0.65 = 73.85 -> 74
 *
 * The generic RESOLUTION_MULTIPLIERS ladder must NOT be used here. fal's curve
 * relative to 2K is 0.38 / 0.46 / 1 / 1.23, nothing like 0.75/1/1.5/3: off a 60
 * base the ladder bills 45 at 480P against a real 24, and 180 at 4K against 74.
 * Two further reasons an explicit branch is required: getResolutionTier() does
 * not recognise "2K" or "768P" and falls through to '720p', and
 * initializePricing() drops the `flatRate` flag once DB pricing loads, which
 * would let a multiplier back in.
 *
 * Mirror of MINIMAX_H3_RATES in server/services/CreditService.ts. The pair is
 * pinned by the two overchargedVideoPricing suites and by
 * minimaxH3Resolutions.test.ts, which duplicate these numbers verbatim.
 */
const MINIMAX_H3_RATES: Record<string, number> = {
  '480P': 24,
  '768P': 28,
  '2K': 60,
  '4K': 74,
};

/** fal's own default and the node definition's default. */
export const MINIMAX_H3_DEFAULT_RESOLUTION = '2K';

/**
 * Client mirror of canonicalMinimaxH3Resolution() in server/config/falModels.ts.
 * Case-insensitive so '4k' and '480p' — the spellings every other video node in
 * this repo uses — land on the right tier.
 *
 * Unlike the server route, this returns a TIER rather than an error for an
 * unrecognised value: the pricing path must stay total. '720p' is the concrete
 * case — selectionCostUtils falls back to it for a node whose
 * parameters.resolution is unset — and it correctly resolves to 2K, which is
 * what such a node will actually render at.
 */
function minimaxH3Tier(resolution?: ResolutionTier | string): string {
  const upper = String(resolution ?? '').trim().toUpperCase();
  return MINIMAX_H3_RATES[upper] !== undefined ? upper : MINIMAX_H3_DEFAULT_RESOLUTION;
}

/**
 * Returns undefined for every other model so callers can early-route, exactly
 * like calculateSeedanceCreditCost() above.
 *
 * Both calculateVideoCreditCost() and calculateCreditCostWithResolution() call
 * this. That is the point: selectionCostUtils calls the LATTER directly, so a
 * branch living only in the former left the multi-select toolbar and whole-flow
 * estimate on a different code path from the Send button. Sharing one function
 * makes them agree by construction rather than by coincidence — and that
 * mattered the moment H3 stopped being single-tier, because the two paths would
 * otherwise disagree at every resolution but 2K.
 *
 * `resolution` is optional and defaults to 2K so pre-existing callers that pass
 * only (modelId, duration) keep billing exactly what they billed before.
 *
 * Mirror of the 'minimax-h3' branch in server/services/CreditService.ts.
 */
export function calculateMinimaxH3CreditCost(
  modelId: string,
  durationSeconds: number | string,
  resolution: ResolutionTier | string = MINIMAX_H3_DEFAULT_RESOLUTION
): number | undefined {
  if (modelId !== 'minimax-h3') return undefined;
  const duration = typeof durationSeconds === 'string' ? parseFloat(durationSeconds) : durationSeconds;
  return MINIMAX_H3_RATES[minimaxH3Tier(resolution)] * Math.max(1, Math.ceil(Number(duration) || 1));
}

/**
 * Hunyuan Video 1.5 — FIXED cost per generation, independent of both duration
 * and resolution.
 *
 * fal's schema has NO `duration` field. Length is `num_frames`, whose default
 * AND maximum are both 121 (~5 seconds), and `resolution` is a const "480p".
 * So neither of the two things the generic pricing tail scales by can actually
 * vary. Billing per requested second meant a 3s selection reserved 105 credits
 * for a clip that always came back ~5s and costs us ~113 — under our own cost.
 *
 * 35 cr/s already IS the 480p rate ($0.075/s is this model's only price), so no
 * resolution multiplier may be applied on top of it.
 *
 * Takes no duration argument ON PURPOSE: a caller cannot accidentally scale it.
 * Called by BOTH calculateVideoCreditCost() and calculateCreditCostWithResolution()
 * for the same reason as the H3 helper above — selectionCostUtils calls the
 * latter directly, so a branch in only the former would leave the multi-select
 * toolbar and whole-flow estimate quoting a number the Send button never bills.
 *
 * Mirror of the 'hunyuan-1.5' branch in server/services/CreditService.ts, which
 * hardcodes the same 35. Both are literals so they must be changed together.
 */
export function calculateHunyuanCreditCost(modelId: string): number | undefined {
  if (modelId !== 'hunyuan-1.5') return undefined;
  return 35 * HUNYUAN_15_FIXED_SECONDS;
}

/**
 * Models whose output length is dictated by a connected AUDIO track rather than
 * by any setting on the node.
 *
 * Kling AI Avatar v2's schema is { image_url, audio_url, prompt } — there is no
 * duration parameter, and the clip comes back exactly as long as the audio. So
 * the billable seconds cannot be read off the node; they have to be measured
 * from the connected file (see probeAudioDuration in aspectRatioUtils).
 */
export const AUDIO_DRIVEN_DURATION_MODELS = new Set(['kling-avatar']);

/**
 * Turn a measured audio duration into the number of seconds we may bill.
 *
 * Returns null for anything unmeasured or nonsensical — not yet probed, NaN,
 * Infinity (streams), zero, negative. **null means "refuse to bill", never
 * "assume a default".** That is the whole point: the previous behaviour billed
 * a 5s/10s dropdown against a clip whose real length was whatever the user
 * connected, which under-charged a 30s track by ~4x. A guessed default would
 * reintroduce exactly that.
 *
 * Rounds UP to a whole second because that is how fal bills, and floors at 1 so
 * a sub-second clip is never free.
 *
 * Shared by the node's cost badge, selectionCostUtils (the multi-select toolbar
 * and whole-flow estimate) and the generation path, so all three agree by
 * construction. The server bills `ceil(units)` from the same number once the
 * adapter sends it.
 */
export function resolveAudioDrivenSeconds(measuredSeconds: unknown): number | null {
  const seconds = typeof measuredSeconds === 'number' ? measuredSeconds : Number(measuredSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return Math.max(1, Math.ceil(seconds));
}

/**
 * Calculate Wan Animate credit cost with custom resolution pricing
 * Wan Animate uses per-second pricing aligned with other video models:
 * - 480p: 24 credits/second (0.75x of base)
 * - 580p: 32 credits/second (base, matches Wan 2.2)
 * - 720p: 40 credits/second (1.25x of base)
 * 
 * This is a V2V model that processes both video input AND image input,
 * so pricing should be comparable to other premium video models.
 * 
 * Note: Both 'replace' and 'move' modes use the same pricing,
 * so only resolution and duration affect the cost.
 * 
 * @param resolution - Resolution string (480p, 580p, or 720p)
 * @param durationSeconds - Duration of the input video in seconds (default: 5)
 * @returns Credit cost for the animation
 */
export function calculateWanAnimateCreditCost(
  resolution: string = '480p',
  durationSeconds: number = 5
): number {
  // Wan Animate per-second pricing based on resolution
  // Aligned with Wan 2.2 (32 credits/sec at 720p) as base reference
  const perSecondRates: Record<string, number> = {
    '480p': 24,   // 0.75x of 580p base
    '580p': 32,   // Base rate, matches Wan 2.2
    '720p': 40,   // 1.25x of 580p base
  };

  const rate = perSecondRates[resolution] || perSecondRates['480p'];
  const duration = Math.max(1, durationSeconds); // Minimum 1 second

  return Math.round(rate * duration * 10) / 10; // Round to 1 decimal place
}

/**
 * Calculate Gemini image generation credit cost per (model, resolution).
 *
 * Gemini's token-based pricing doesn't scale linearly with pixel area, so the
 * standard base720p × resolution-tier multiplier produces wrong numbers
 * (notably: 3 Pro costs the same at 1K and 2K, while 3.1 Flash scales roughly
 * with pixel count). Use an explicit table of Google-direct prices and apply
 * the target margin uniformly.
 *
 * Source: ai.google.dev/gemini-api/docs/pricing (verified Nov 2025).
 * Target: 50% gross margin against Google direct cost. fal.ai routing carries
 * ~15-20% upstream markup, which compresses the margin to ~35-40% — acceptable
 * during the in-flight migration to Google direct, but the math here assumes
 * Google direct as the destination.
 *
 * Must match server CreditService.calculateCost() for these model IDs.
 */
export function calculateGeminiImageCreditCost(modelId: string, resolution: string = '1024px'): number {
  // Google direct API cost in USD per generation
  const COSTS_USD: Record<string, Record<string, number>> = {
    // 2.5 Flash (Banana Stable) — flat $0.039 regardless of resolution
    'gemini-2.5-flash-image': { '512px': 0.039, '1024px': 0.039, '2048px': 0.039, '4096px': 0.039 },
    'gemini-nano-banana':     { '512px': 0.039, '1024px': 0.039, '2048px': 0.039, '4096px': 0.039 },
    // 3 Pro (Banana Pro) — 1K and 2K cost the same; 4K jumps to $0.24
    'gemini-3-pro-image-preview': { '1024px': 0.134, '2048px': 0.134, '4096px': 0.240 },
    'gemini-nano-banana-pro':     { '1024px': 0.134, '2048px': 0.134, '4096px': 0.240 },
    // 3.1 Flash (Banana 2 Fast) — scales per resolution
    'gemini-3.1-flash-image': { '512px': 0.045, '1024px': 0.067, '2048px': 0.101, '4096px': 0.151 },
    // 3.1 Flash Lite (Banana 2 Lite) — 1K only. Every tier is priced at the 1K
    // rate so a stale 2K/4K value carried over from another model cannot miss
    // the lookup and fall through to Banana 2's more expensive row.
    'gemini-3.1-flash-lite-image': { '512px': 0.0336, '1024px': 0.0336, '2048px': 0.0336, '4096px': 0.0336 },
  };

  const costUSD = COSTS_USD[modelId]?.[resolution] ?? COSTS_USD['gemini-3.1-flash-image']['1024px'];
  const userPriceUSD = costUSD * 2; // 50% margin
  const creditValueUSD = 0.00333;
  return Math.ceil(userPriceUSD / creditValueUSD);
}

/**
 * Calculate OpenAI GPT Image generation credit cost.
 *
 * OpenAI prices these models per-(model × quality tier × resolution). Quality
 * tier alone spans a ~40× range upstream ($0.005 low → $0.211 high for
 * gpt-image-2 at 1024px), so flat per-model pricing massively overcharges
 * "low" users and undercharges "high" users.
 *
 * USD prices below are per generation at the listed resolution (sourced from
 * OpenAI pricing page, verified May 2026):
 *
 *   gpt-image-2:
 *     1024px  → low $0.005, medium $0.053, high $0.211
 *     2048px  → low $0.011, medium $0.063, high $0.252 (~1.2-1.5× 1024 rates)
 *   gpt-image-1.5:
 *     1024px  → low $0.005, medium $0.040, high $0.150
 *     2048px  → low $0.011, medium $0.048, high $0.180
 *   gpt-image-1-mini (cheapest active OpenAI image model):
 *     1024px  → low $0.005, medium $0.020, high $0.052
 *     2048px  → low $0.008, medium $0.024, high $0.063
 *
 * Margin target: ~35% gross (matches the rest of the image catalog post-2026
 * repricing). Credit = ceil(USD × 300 / 0.65) ≈ ceil(USD × 461) — we use 450
 * for round-number consistency with the rest of the codebase.
 *
 * Default quality is "medium" when omitted (matches GPT Image node default).
 *
 * Must match server CreditService.calculateCost() for these model IDs.
 */
export function calculateGPTImageCreditCost(
  modelId: string,
  quality: string = 'medium',
  resolution: string = '1024px'
): number {
  // USD cost per image, indexed by [model][resolution][quality]
  const COSTS_USD: Record<string, Record<string, Record<string, number>>> = {
    'gpt-image-2': {
      '1024px': { low: 0.005, medium: 0.053, high: 0.211 },
      '2048px': { low: 0.011, medium: 0.063, high: 0.252 },
    },
    'gpt-image-1.5': {
      '1024px': { low: 0.005, medium: 0.040, high: 0.150 },
      '2048px': { low: 0.011, medium: 0.048, high: 0.180 },
    },
    'gpt-image-1-mini': {
      '1024px': { low: 0.005, medium: 0.020, high: 0.052 },
      '2048px': { low: 0.008, medium: 0.024, high: 0.063 },
    },
  };

  const modelCosts = COSTS_USD[modelId];
  if (!modelCosts) {
    // Unknown GPT-image variant — fall back to legacy flat pricing
    return calculateCreditCostWithResolution(modelId, 1, resolution);
  }
  const resTable = modelCosts[resolution] ?? modelCosts['1024px'];
  const q = (quality || 'medium').toLowerCase();
  const costUSD = resTable[q] ?? resTable['medium'];

  // 35% margin: credits = ceil(USD × 300 / 0.65). Use 450 for consistency
  // with the rest of the pricing math in this file (gpt-image-2 migration
  // used the same multiplier).
  return Math.ceil(costUSD * 450);
}

/**
 * VideoScale (fal.ai bytedance-upscaler) ratios, relative to the 1080p base.
 *
 * fal charges per second of output and roughly doubles per resolution step
 * (1080p $0.0072/s, 2K $0.0144/s, 4K $0.0288/s at 30fps standard), which is NOT
 * the platform's generic 1x/1.5x/3x ladder. These ratios reproduce fal's real
 * curve from whatever the 1080p base is.
 *
 * Shared by client and server. Must match VIDEOSCALE_RESOLUTION_RATIOS in
 * server/services/CreditService.ts.
 */
const VIDEOSCALE_RESOLUTION_RATIOS: Record<string, number> = {
  '1080p': 1,
  '1440p': 1.8,
  '2k': 1.8,
  '4k': 3.6,
};

/** Base credits per second at 1080p, used until DB pricing loads. */
const VIDEOSCALE_FALLBACK_BASE = 5;

/**
 * Calculate VideoScale (fal.ai bytedance-upscaler) credit cost.
 *
 * The base rate now comes from the model_registry row rather than a hardcoded
 * table, so that the number we PUBLISH (/api/models, /api/credits/pricing), the
 * number an admin controls (PUT /api/admin/pricing/:id) and the number we CHARGE
 * are the same number. Before migration 123 they were three different things:
 * the row said "10 credits per generation" and was never read, while billing
 * came from a hardcoded 5/9/18 per SECOND — so a 4K 10s enhance published as 10
 * credits and charged 180.
 *
 * At the migration 123 row (5 cr/second) the ratios reproduce the previous
 * hardcoded table exactly: 5 / 9 / 9 / 18. No user's bill changes.
 *
 * The `unit === 'second'` guard is deliberate: until migration 123 lands the row
 * still says 'generation' with a value of 10, and trusting it would silently
 * DOUBLE every upscale. With the guard, code and migration are safe in either
 * order.
 *
 * Must match server CreditService.calculateCost() for 'bytedance-upscaler' exactly.
 */
export function calculateVideoScaleCreditCost(
  resolution: string = '1080p',
  durationSeconds: number = 5
): number {
  const row = MODEL_BASE_COSTS['bytedance-upscaler'];
  const base = row && row.unit === 'second' ? row.base720p : VIDEOSCALE_FALLBACK_BASE;
  const ratio = VIDEOSCALE_RESOLUTION_RATIOS[resolution] ?? VIDEOSCALE_RESOLUTION_RATIOS['1080p'];
  const duration = Math.max(1, durationSeconds);
  return Math.ceil(base * ratio * duration);
}

/**
 * Calculate image generation credit cost with resolution
 * @param modelId - Image model ID
 * @param resolution - Resolution tier or string
 * @returns Credit cost for the image
 */
export function calculateImageCreditCost(modelId: string, resolution: ResolutionTier | string = '720p'): number {
  // Non-linear models (e.g. kling-image-o3) are routed inside
  // calculateCreditCostWithResolution so that direct callers get the same number.
  return calculateCreditCostWithResolution(modelId, 1, resolution);
}

/**
 * Calculate Kling Image credit cost.
 *
 * fal.ai states: "Your request will cost $0.028 per image for 1K/2K, price will
 * be double for 4K". So 1K and 2K are the SAME price and only 4K steps up —
 * NOT the standard 1x/2x/3x resolution ladder.
 *
 *   1K / 2K : $0.028 → ceil(0.028 × 450) = 13 credits
 *   4K      : $0.056 → ceil(0.056 × 450) = 26 credits
 *
 * The ×450 factor is the catalog-wide image conversion (1 credit = $0.00333, so
 * cost is USD × 300; charging USD × 450 yields the ~35% gross margin target).
 *
 * MUST match server CreditService.calculateCost()'s `kling-image-o3` branch and
 * migration 104's credits_per_unit baseline. A mismatch is silent: the button
 * quotes one number and the balance drops by another.
 *
 * @param modelId - 'kling-image-o3'
 * @param resolution - '1K' | '2K' | '4K' (anything else falls back to 1K)
 */
export function calculateKlingImageCreditCost(
  _modelId: string,
  resolution: string = '1K'
): number {
  const KLING_IMAGE_COSTS_USD: Record<string, number> = {
    '1K': 0.028,
    '2K': 0.028,
    '4K': 0.056,
  };
  const costUSD = KLING_IMAGE_COSTS_USD[String(resolution)] ?? KLING_IMAGE_COSTS_USD['1K'];
  return Math.ceil(costUSD * 450);
}

/**
 * Grok Imagine Image 2.0 (xAI direct) — per-(quality × resolution) pricing.
 *
 * xAI's API listing (GET /v1/image-generation-models, 2026-09-18) prices the
 * model per image: low 1k $0.04 · 1.5k $0.05 · 2k $0.06; medium 1k $0.06 ·
 * 1.5k $0.07 · 2k $0.08. Credits = ceil(USD × 450), the catalog image factor
 * that also priced grok-image (ceil(0.02 × 450) = 9). Written out, not
 * computed, so float noise cannot move a tier. Edits add +$0.01 per input
 * image, linear (measured: 1-input low/1k $0.05, 3-input medium/2k $0.11)
 * → 5 credits each.
 *
 * MUST match server/config/grokImage.ts (GROK_IMAGE_2_CREDITS,
 * GROK_IMAGE_2_INPUT_IMAGE_CREDITS), which CreditService.calculateCost() bills
 * from. src/utils/__tests__/grokImage2Pricing.test.ts asserts equality.
 */
export const GROK_IMAGE_2_MODEL_ID = 'grok-imagine-image-2.0';
export const GROK_IMAGE_2_QUALITIES = ['low', 'medium'] as const;
export const GROK_IMAGE_2_RESOLUTIONS = ['1k', '1.5k', '2k'] as const;
export const GROK_IMAGE_2_MAX_INPUT_IMAGES = 5;
export const GROK_IMAGE_2_CREDITS: Readonly<Record<string, Readonly<Record<string, number>>>> = {
  low: { '1k': 18, '1.5k': 23, '2k': 27 },
  medium: { '1k': 27, '1.5k': 32, '2k': 36 },
};
export const GROK_IMAGE_2_INPUT_IMAGE_CREDITS = 5;

/** Same normalisation as the server: unknown / 'auto' / missing → low, 1k. */
export function normalizeGrokImage2Tier(
  quality?: unknown,
  resolution?: unknown
): { quality: 'low' | 'medium'; resolution: '1k' | '1.5k' | '2k' } {
  const q = typeof quality === 'string' ? quality.toLowerCase() : '';
  const r = typeof resolution === 'string' ? resolution.toLowerCase() : '';
  return {
    quality: (GROK_IMAGE_2_QUALITIES as readonly string[]).includes(q) ? (q as 'low' | 'medium') : 'low',
    resolution: (GROK_IMAGE_2_RESOLUTIONS as readonly string[]).includes(r) ? (r as '1k' | '1.5k' | '2k') : '1k',
  };
}

export function calculateGrokImage2CreditCost(
  _modelId: string,
  quality?: unknown,
  resolution?: unknown,
  inputImageCount: number = 0
): number {
  const tier = normalizeGrokImage2Tier(quality, resolution);
  const n = Math.floor(Number(inputImageCount));
  const inputs = Number.isFinite(n) && n > 0 ? Math.min(n, GROK_IMAGE_2_MAX_INPUT_IMAGES) : 0;
  return GROK_IMAGE_2_CREDITS[tier.quality][tier.resolution] + inputs * GROK_IMAGE_2_INPUT_IMAGE_CREDITS;
}

/**
 * Best-effort count of the images a node receives from its DIRECT upstream
 * sources, for quoting input-image surcharges: an Output node whose current
 * page is an image, or an Image Container (imagePanel) holding one. Mirrors the
 * two sources the image handlers read. The server counts what it actually sends
 * and bills that, so this can only ever make the quote more accurate.
 */
export function countImagesFromSources(
  sources: Array<{ type?: string; data?: Record<string, unknown> } | undefined>
): number {
  let count = 0;
  for (const src of sources) {
    if (!src?.data) continue;
    if (src.type === 'customOutput' || src.type === 'universalOutput') {
      const pages = (src.data.pages || []) as Array<{ content?: string; type?: string }>;
      const idx = (src.data.currentPageIndex || 0) as number;
      if (pages[idx]?.type === 'image' && pages[idx]?.content) count += 1;
    } else if (src.type === 'imagePanel' && src.data.imageUrl) {
      count += 1;
    }
  }
  return count;
}

/**
 * Credit cost of one Grok Image node generation, for every quoting path (the
 * node badge in NodeAdapters and selectionCostUtils). One helper so the two
 * cannot diverge — the CLAUDE.md "selectionCostUtils bypass" gotcha.
 *
 * Only grok-imagine-image-2.0 reads quality / resolution / input images; the
 * older Grok image models are flat per generation.
 */
export function calculateGrokImageNodeCreditCost(
  selectedModel: string,
  parameters: Record<string, unknown> = {},
  inputImageCount: number = 0
): number {
  if (selectedModel === GROK_IMAGE_2_MODEL_ID) {
    return calculateGrokImage2CreditCost(selectedModel, parameters.quality, parameters.resolution, inputImageCount);
  }
  return calculateCreditCostWithResolution(selectedModel, 1, '720p');
}

/**
 * Calculate ElevenLabs audio credit cost.
 *
 * TTS (Voice) is CHARACTER-based: cost = ceil(perThousand × chars / 1000), min 1.
 *   eleven_v3 / eleven_multilingual_v2: $0.10/1k → 46 cr/1k.
 *   eleven_flash_v2_5 / eleven_turbo_v2_5: $0.05/1k → 23 cr/1k.
 * SFX / Music are flat per-generation (no duration is passed here): the flat
 * comes from MODEL_BASE_COSTS and reflects a sensible default length priced at
 * SFX 1 cr/s (10s → 10) and Music 1.2 cr/s (30s default → 36). Rates mirror
 * server CreditService.calculateCost()'s elevenlabs branch + FALLBACK_MODEL_COSTS.
 *
 * @param modelId - eleven_v3 | eleven_flash_v2_5 | eleven_turbo_v2_5 |
 *                  eleven_multilingual_v2 | eleven_text_to_sound_v2 | music_v1 | music_v2
 * @param charCount - Text length (only used for TTS models). Defaults to 0.
 */
export function calculateElevenLabsTTSCreditCost(modelId: string, charCount: number = 0): number {
  const TTS_CREDITS_PER_1K: Record<string, number> = {
    eleven_v3: 46,
    eleven_multilingual_v2: 46,
    eleven_flash_v2_5: 23,
    eleven_turbo_v2_5: 23,
  };
  const per1k = TTS_CREDITS_PER_1K[modelId];
  if (per1k) {
    return Math.max(1, Math.ceil((per1k / 1000) * Math.max(0, charCount)));
  }
  // SFX / Music — flat per-generation from the base cost table.
  const flat = MODEL_BASE_COSTS[modelId]?.base720p;
  return typeof flat === 'number' ? flat : 1;
}

/**
 * Calculate LTX-2.3 credit cost with explicit T2V vs I2V mode split.
 *
 * Per fal.ai pricing (Nov 2025):
 *   T2V: $0.08/s @1080p, $0.16/s @1440p, $0.32/s @2160p  (33% premium)
 *   I2V: $0.06/s @1080p, $0.12/s @1440p, $0.24/s @2160p  (legacy/baseline)
 *   Fast T2V: $0.04/s @1080p (Fast tier — no I2V at this price)
 *
 * Mode is inferable from input shape in the node adapter: when an `image_url`
 * input is connected to the LTX node, treat as I2V; otherwise T2V. This helper
 * accepts the mode explicitly so callers (NodeAdapters, server CreditService)
 * can compute the right price without re-inspecting the graph.
 *
 * Must match server CreditService.calculateCost() for ltx-2.3 models.
 *
 * @param modelId - 'ltx-2.3', 'ltx-2.3-pro', or 'ltx-2.3-fast'
 * @param durationSeconds - Video duration in seconds
 * @param resolution - '1080p' | '1440p' | '2160p' (4K)
 * @param mode - 'text-to-video' (T2V) or 'image-to-video' (I2V)
 * @returns Credit cost
 */
export function calculateLTX23CreditCost(
  modelId: string,
  durationSeconds: number,
  resolution: string = '1080p',
  mode: 'text-to-video' | 'image-to-video' = 'image-to-video'
): number {
  const isFast = modelId === 'ltx-2.3-fast';
  // Per-resolution per-second credits at 35% margin against fal.ai's API prices.
  //
  // T2V AND I2V COST THE SAME (migration 129). fal's pages for BOTH
  // fal-ai/ltx-2.3/text-to-video and .../image-to-video carry the identical
  // sentence: "Your request will cost $0.08 per second for 1080p, $0.16 per
  // second for 1440p or $0.32 per second for 2160p." The i2v column used to
  // hold the $0.06/$0.12/$0.24 rate — that is the FAST tier's price, which
  // appears on the non-fast page as a contaminating sidebar table (see the
  // migration 129 header). It undercharged ~25% at every tier.
  //
  // ceil(usd * 300 / 0.65):
  //   1080p  0.08 -> 24.00 / 0.65 =  36.92 ->  37
  //   1440p  0.16 -> 48.00 / 0.65 =  73.85 ->  74
  //   2160p  0.32 -> 96.00 / 0.65 = 147.69 -> 148
  //
  // The columns are deliberately kept as a { t2v, i2v } pair rather than
  // collapsed: the server still remaps ltx-2.3 -> ltx-2.3-t2v as a distinct
  // SKU, and if fal ever re-splits the price the shape is already here. While
  // they are equal, `mode` cannot change the number — which is what finally
  // makes the badge, the toolbar and the bill agree for this model.
  const PRICING: Record<string, { t2v: number; i2v: number }> = {
    '1080p': { t2v: 37, i2v: 37 },   // $0.08/s both modes
    '1440p': { t2v: 74, i2v: 74 },   // $0.16/s both modes
    '2160p': { t2v: 148, i2v: 148 }, // $0.32/s both modes
    '4k':    { t2v: 148, i2v: 148 }, // 2160p alias
  };

  const duration = Math.max(1, Math.ceil(durationSeconds));
  const resKey = (resolution === '720p' || resolution === '1080p') ? '1080p'
    : resolution === '1440p' ? '1440p'
    : (resolution === '4k' || resolution.includes('2160') || resolution.includes('4k') || resolution.includes('4K')) ? '2160p'
    : '1080p';

  if (isFast) {
    // Fast has its own three-tier table and its own >10s downgrade rule; see
    // calculateLtx23FastCreditCost() below. It used to be a flat 19 here.
    return calculateLtx23FastCreditCost(modelId, durationSeconds, resolution)!;
  }

  const tier = PRICING[resKey] || PRICING['1080p'];
  const rate = mode === 'text-to-video' ? tier.t2v : tier.i2v;
  return rate * duration;
}

/**
 * LTX-2.3 Fast (fal.ai) per-second credit rates, by EFFECTIVE resolution.
 *
 * fal, verbatim on both fast endpoints (fetched 2026-09-04): "Your request will
 * cost $0.06 per second for 1080p, $0.12 per second for 1440p or $0.24 per
 * second for 2160p."
 *
 * Rates via ceil(usd × 300 / 0.65):
 *   1080p  0.06 × 300 = 18 ; 18 / 0.65 =  27.69 ->  28
 *   1440p  0.12 × 300 = 36 ; 36 / 0.65 =  55.38 ->  56
 *   2160p  0.24 × 300 = 72 ; 72 / 0.65 = 110.77 -> 111
 *
 * This REPLACES a flat 19 cr/s that was derived from a $0.04/s price. 1440p —
 * which the node offered and the route sent — billed 19 against a $0.12/s cost
 * of ~36 credits, losing about $0.057 for every second of video generated.
 *
 * EFFECTIVE, not requested: fal restricts durations over 10 seconds to 1080p
 * (the constraint is in the schema, not just the docs) and the route downgrades
 * to match, so a 1440p + 12s selection renders at 1080p. Billing the requested
 * tier would charge double for it. LTX_23_FAST_EFFECTIVE_RESOLUTION below is
 * the client mirror of effectiveLtx23FastResolution() in
 * server/config/falModels.ts, which the route and the server's billing table
 * both call — one rule, three consumers.
 *
 * Mirror of LTX_23_FAST_RATES in server/services/CreditService.ts. The pair is
 * pinned by the two ltx23FastResolutions suites.
 */
const LTX_23_FAST_RATES: Record<string, number> = {
  '1080p': 28,
  '1440p': 56,
  '2160p': 111,
};

/** Client mirror of clampLtx23Duration(duration, true) — fal even-integer enum, 6..20. */
function clampLtx23FastDuration(duration: number | string): number {
  const n = typeof duration === 'string' ? parseFloat(duration) : duration;
  const rounded = Math.round((Number(n) || 6) / 2) * 2;
  return Math.max(6, Math.min(20, rounded));
}

/** Client mirror of effectiveLtx23FastResolution(). */
export function ltx23FastEffectiveResolution(
  resolution: string | undefined,
  durationSeconds: number | string
): string {
  if (clampLtx23FastDuration(durationSeconds) > 10) return '1080p';
  const lower = String(resolution ?? '').trim().toLowerCase();
  const normalised = lower === '4k' ? '2160p' : lower;
  return LTX_23_FAST_RATES[normalised] !== undefined ? normalised : '1080p';
}

/**
 * Returns undefined for every other model so callers can early-route, exactly
 * like calculateMinimaxH3CreditCost() above.
 *
 * Called by calculateVideoCreditCost() AND calculateCreditCostWithResolution()
 * for the reason CLAUDE.md spells out: selectionCostUtils calls the latter
 * directly. Before this, ltx-2.3-fast had NO branch in that function at all, so
 * the multi-select toolbar priced it off MODEL_BASE_COSTS (18) times the
 * resolution ladder — quoting 27 at 1080p against a server charge of 19. That
 * divergence was live.
 */
export function calculateLtx23FastCreditCost(
  modelId: string,
  durationSeconds: number | string,
  resolution: string = '1080p'
): number | undefined {
  if (modelId !== 'ltx-2.3-fast') return undefined;
  const duration = typeof durationSeconds === 'string' ? parseFloat(durationSeconds) : durationSeconds;
  const seconds = Math.max(1, Math.ceil(Number(duration) || 1));
  return LTX_23_FAST_RATES[ltx23FastEffectiveResolution(resolution, seconds)] * seconds;
}

/**
 * The LTX-2.3 PRO ids — the non-fast family. `ltx-2.3-t2v` is the SKU
 * server/routes/falai.ts remaps `ltx-2.3` onto when no image is connected; it
 * is listed here so a saved flow or an admin surface pricing under that id
 * takes the same table rather than falling through to the generic ladder.
 */
const LTX_23_PRO_IDS = new Set(['ltx-2.3', 'ltx-2.3-pro', 'ltx-2.3-t2v']);

/**
 * Gated wrapper around calculateLTX23CreditCost() for the non-fast family, so
 * BOTH cost entry points can early-route it.
 *
 * Before migration 129 neither calculateVideoCreditCost() nor
 * calculateCreditCostWithResolution() had any ltx-2.3 branch: the model fell
 * through to MODEL_BASE_COSTS (28) times the resolution ladder, so at 1080p the
 * multi-select toolbar and the whole-flow estimate quoted 42 while the server
 * billed 28. That is the divergence CLAUDE.md records for this exact model
 * ("ltx-2.3 came to quote 28 and bill 42") — it had never actually been closed,
 * only documented.
 *
 * Mode defaults to image-to-video, matching the node badge's choice in
 * NodeAdapters. Since migration 129 made t2v and i2v the same price, the
 * default can no longer be wrong.
 */
export function calculateLtx23ProCreditCost(
  modelId: string,
  durationSeconds: number | string,
  resolution: string = '1080p',
  mode: 'text-to-video' | 'image-to-video' = 'image-to-video'
): number | undefined {
  if (!LTX_23_PRO_IDS.has(modelId)) return undefined;
  const duration = typeof durationSeconds === 'string' ? parseFloat(durationSeconds) : durationSeconds;
  return calculateLTX23CreditCost(modelId, Number(duration) || 1, resolution, mode);
}

/**
 * Format credit cost for display
 * @param cost - Credit cost
 * @returns Formatted string with commas (e.g., "10,000.0" or "5.0")
 */
export function formatCreditCost(cost: number): string {
  return cost.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * Get credit cost range for a model (min at 720p, max at 4K)
 * @param modelId - The model identifier
 * @returns Object with min, max, unit type, and formatted display string
 */
export function getModelCreditRange(modelId: string): {
  min: number;
  max: number;
  unit: 'generation' | 'second';
  displayText: string;
} | null {
  const normalizedId = modelId.toLowerCase().replace(/_/g, '-');
  const modelCost = MODEL_BASE_COSTS[modelId] || MODEL_BASE_COSTS[normalizedId];
  
  if (!modelCost) {
    return null;
  }
  
  const min = modelCost.base720p;
  const max = Math.ceil(min * RESOLUTION_MULTIPLIERS['4k']); // 3x for 4K
  
  let displayText: string;
  if (modelCost.unit === 'second') {
    displayText = `${min}-${max}/s`;
  } else {
    displayText = `${min}-${max}`;
  }
  
  return {
    min,
    max,
    unit: modelCost.unit,
    displayText
  };
}
